// TelcoTrack - Object Service
// Service for object API communication

import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of, Subject, throwError, timer, from } from 'rxjs';
import { map, catchError, timeout, switchMap, take, filter, tap, finalize, share, concatMap, reduce } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { LoggerService } from './logger.service';
import { getStatusColor } from '../../shared/utils/object.utils';
import { fetchPhotoBlob } from '../../shared/utils/photo-download-fetch.util';
import {
  Object,
  ObjectQueryDto,
  ObjectResponse,
  MapObject,
  ObjectTypeDefinition,
  ObjectStatusDefinition,
  GeoLocation,
  AddressPointMap,
  WorkReportRow,
  ManagerDashboardMetrics,
  ManagerDashboardTimeseriesResult,
  PolygonWorkReportInvoiceSummary,
  ObjectAcquisition,
  ObjectAcquisitionPatch,
  AcquisitionAnalyzeResult,
  AcquisitionCommitResult,
  RozvlakneniAnalyzeResult,
  RozvlakneniCommitResult,
  RopidAnalyzeResult,
  RopidCommitResult,
  UnifiedImportAnalyzeResult,
  UnifiedImportCommitResult,
  ImportUploadKind,
  ImportUploadedFileRecord,
  PolygonImportStatusRow,
  ImportUploadDeleteResult,
  TrimVykopOdbockyRun,
  NavaznostAnalyzeResult,
  NavaznostBuildingLinkList,
  NavaznostCommitResult,
  NavaznostLinkInput,
  NavaznostManualAssignments,
  PpdParseResult,
  ProtelcoCatalogItem,
  ProtelcoCatalogAnalyzeResult,
  ProtelcoCatalogImportResult,
  ProtelcoCatalogMeta,
  PpdAnalyzeResult,
  PpdCommitResult,
  FinanceComparisonResult,
  FinanceMaterialComparisonResult,
  FinanceWorkComparisonResult,
  RozvadecWeldLayoutPutDto,
  RozvadecWeldLayoutResponseJson,
  RozvadecRsuOccupancyPutDto,
  RozvadecRsuOccupancyResponseJson,
  RozvadecOccupancySchemaResponseJson,
} from '../models/object.model';
import { TreeNode } from '../models/component.model';
import {
  STATUS_COLORS,
  CZECH_STATUS,
  STATUS_RED,
  STATUS_YELLOW,
  STATUS_GREEN,
  ACQUISITION_STATUS,
  akviziceDrawingStatusAcqForBuildingType,
  buildAuthorityCatalogApplyByPolygon,
  type BudovaBuildingType,
  type WorkReportPaymentStatus,
} from '@core/shared-bridge';
import {
  CreateMaterialUsagePayload,
  MaterialUsageRow,
  UpdateMaterialUsagePayload,
} from '../models/material-usage.model';
import type { ResolvePathResponse } from '../../features/maps/components/map/core/map.types';
// Import shared constants (single source of truth)
// Using relative path that works in Docker via volume mount
// In Docker: /app is frontend/web-app, so ../../../../../../shared goes to project root/shared
// In local dev, this also works as the path is relative to the file

/** Query for `GET /api/objects/materials-used` (paginated). */
export interface MaterialsUsedQueryParams {
  months?: number;
  month?: string;
  dateFrom?: string;
  dateTo?: string;
  limit?: number;
  offset?: number;
  changedBy?: string;
  polygonId?: string;
  mainCategory?: string;
  objectType?: string;
  objectStatus?: string;
  includeFacets?: boolean;
  facetPhaseForType?: string;
  /** Restrict to active construction folders when no explicit polygon filter. */
  activePolygonOnly?: boolean;
  /** Restrict to plan_status stavba (finance portfolio default). */
  stavbaPolygonOnly?: boolean;
}

export interface MaterialsUsedPageItem {
  usageId: string;
  month: string;
  changedBy: string;
  polygonId: string | null;
  objectId: string;
  objectType: string;
  objectStatus: string | null;
  objectMetadata?: Record<string, unknown> | null;
  sapCode: string;
  name: string;
  quantity: number;
  unit: string | null;
  price: number | null;
  lineTotal: number | null;
  mainCategory: string | null;
  subCategory?: string | null;
  branch1?: string | null;
  branch2?: string | null;
  branch3?: string | null;
  branch4?: string | null;
  supplier: string | null;
  usedAt?: string;
  /** AutoCRM GoodsIssue.id z auto-výdeje (pokud vznikla). */
  warehouseGoodsIssueId?: string;
  /** Stav výdejky nebo skip důvod. */
  warehouseGoodsIssueStatus?: string;
}

export interface MaterialsUsedFacets {
  users: string[];
  polygonIds: string[];
  categories: string[];
  objectTypes: string[];
  phaseCodes: string[];
}

export interface MaterialsUsedPageResponse {
  items: MaterialsUsedPageItem[];
  totalCount: number;
  aggregateLineTotal: number;
  facets?: MaterialsUsedFacets;
}

/** Reporting: spotřeba na polygon/stavbu. */
export interface MaterialConsumptionByPolygonRow {
  polygonId: string;
  polygonName: string | null;
  quantity: number;
  lineTotal: number;
  pricedQuantity: number;
  usageRowCount: number;
}

export interface MaterialConsumptionByPolygonReport {
  items: MaterialConsumptionByPolygonRow[];
  totals: { quantity: number; lineTotal: number; usageRowCount: number };
}

export type MaterialUsageHistoryOptions = { skipHistoryAudit?: boolean };

function materialUsageHttpParams(opts?: MaterialUsageHistoryOptions): { params?: HttpParams } {
  if (!opts?.skipHistoryAudit) return {};
  return { params: new HttpParams().set('skipHistoryAudit', '1') };
}

@Injectable({
  providedIn: 'root'
})
export class ObjectService {
  private readonly apiUrl = `${environment.apiUrl}${environment.objectServiceUrl}`;

  /**
   * Emituje se po každém úspěšném `commitSession` a obsahuje
   * seznam dotčených `polygonIds` vrácený backendem. Slouží globálním konzumentům,
   * kteří potřebují reagovat na dokončený commit.
   */
  private readonly commitCompletedSubject = new Subject<{ polygonIds: string[] }>();
  readonly commitCompleted$ = this.commitCompletedSubject.asObservable();

  /** Dedupe concurrent GET last-path-advance-batch for the same segment (compact VYKOP popup). */
  private readonly lastPathAdvanceBatchInflight = new Map<
    string,
    Observable<{
      id: string;
      segmentIds: string[];
      allRouteSegmentIds: string[];
      updatedAt?: string;
    } | null>
  >();

  constructor(
    private readonly http: HttpClient,
    private readonly logger: LoggerService
  ) {}

  /**
   * Get all objects with filtering and pagination.
   * Returns { objects, total } for correct pagination.
   */
  getAllObjects(query?: ObjectQueryDto): Observable<{ objects: Object[]; total: number }> {
    let params = new HttpParams();

    if (query) {
      Object.keys(query).forEach(key => {
        const value = query[key as keyof ObjectQueryDto];
        if (value !== undefined && value !== null) {
          if (key === 'location' && typeof value === 'object' && 'center' in value) {
            const locationValue = value as { center: GeoLocation; radius: number };
            params = params.set('centerLng', locationValue.center.coordinates[0].toString());
            params = params.set('centerLat', locationValue.center.coordinates[1].toString());
            params = params.set('radius', locationValue.radius.toString());
          } else if (Array.isArray(value)) {
            value.forEach(item => {
              const stringValue = typeof item === 'object' ? JSON.stringify(item) : String(item);
              params = params.append(key, stringValue);
            });
          } else {
            const stringValue = typeof value === 'object' ? JSON.stringify(value) : String(value);
            params = params.set(key, stringValue);
          }
        }
      });
    }

    return this.http.get<any>(`${this.apiUrl}`, { params })
      .pipe(
        map(response => {
          if (response.success && response.data) {
            const objects = Array.isArray(response.data.objects) ? response.data.objects : [];
            const total = typeof response.data.total === 'number' ? response.data.total : objects.length;
            return { objects, total };
          }
          this.logger.warn('Unexpected object response format:', response);
          return { objects: [], total: 0 };
        }),
        catchError(this.handleError)
      );
  }


  /**
   * Map bulk load by ids — 1 RTT vs N× GET /objects/:id (route ensure / branch restore).
   * Returns bbox-minimal map objects; missing ids are omitted.
   */
  getObjectsByIds(ids: string[]): Observable<Object[]> {
    const unique = [...new Set(ids.map((id) => id.trim()).filter(Boolean))];
    if (unique.length === 0) {
      return of([]);
    }
    const chunks: string[][] = [];
    for (let i = 0; i < unique.length; i += 500) {
      chunks.push(unique.slice(i, i + 500));
    }
    return from(chunks).pipe(
      concatMap((chunk) =>
        this.http
          .post<{ success: boolean; data?: { objects?: Object[] } }>(`${this.apiUrl}/by-ids`, {
            ids: chunk,
          })
          .pipe(
            map((res) => (res?.success && Array.isArray(res.data?.objects) ? res.data!.objects! : [])),
          ),
      ),
      reduce((acc: Object[], part: Object[]) => acc.concat(part), [] as Object[]),
      catchError(this.handleError),
    );
  }

  /**
   * Lazy HP / Dig. patch for map attribute filters (not included in default bbox).
   * Chunks to 500 ids — same cap as {@link getObjectsByIds}.
   */
  getAttributeFilterFields(
    ids: string[],
  ): Observable<Array<{ id: string; hpCountTotal: number | null; digitalniCesta: number | null }>> {
    const unique = [...new Set(ids.map((id) => id.trim()).filter(Boolean))];
    if (unique.length === 0) {
      return of([]);
    }
    const chunks: string[][] = [];
    for (let i = 0; i < unique.length; i += 500) {
      chunks.push(unique.slice(i, i + 500));
    }
    type AttrRow = { id: string; hpCountTotal: number | null; digitalniCesta: number | null };
    return from(chunks).pipe(
      concatMap((chunk) =>
        this.http
          .post<{ success: boolean; data?: { objects?: AttrRow[] } }>(
            `${this.apiUrl}/attribute-filter-fields`,
            { ids: chunk },
          )
          .pipe(
            map((res) =>
              res?.success && Array.isArray(res.data?.objects) ? res.data!.objects! : [],
            ),
          ),
      ),
      reduce((acc: AttrRow[], part: AttrRow[]) => acc.concat(part), [] as AttrRow[]),
      catchError(this.handleError),
    );
  }

  /** Sdílené probíhající GET /objects/:id — popup open může volat stejné id z více větví najednou. */
  private readonly objectByIdInflight = new Map<string, Observable<Object>>();

  /**
   * Get object by ID. API returns { success, data } with data: { id, description, metadata, ... }.
   * Concurrent calls for the same id+options share one HTTP request (map popup open, horizontal propoj highlight).
   * Map popup should pass `{ includeTimeline: false }` — history loads via getObjectHistoryTimeline when needed.
   */
  getObjectById(
    id: string,
    options?: { includeTimeline?: boolean },
  ): Observable<Object> {
    const objectId = id.trim();
    const includeTimeline = options?.includeTimeline !== false;
    const inflightKey = `${objectId}|tl=${includeTimeline ? '1' : '0'}`;
    const inflight = this.objectByIdInflight.get(inflightKey);
    if (inflight) return inflight;

    const params = includeTimeline ? undefined : { includeTimeline: 'false' };
    const req = this.http
      .get<{ success: boolean; data?: Object }>(`${this.apiUrl}/${objectId}`, { params })
      .pipe(
      map(res => {
        if (res?.success && res.data && !Array.isArray(res.data)) return res.data;
        throw new Error('Object not found');
      }),
      catchError(this.handleError),
      finalize(() => {
        if (this.objectByIdInflight.get(inflightKey) === req) {
          this.objectByIdInflight.delete(inflightKey);
        }
      }),
      share(),
    );
    this.objectByIdInflight.set(inflightKey, req);
    return req;
  }

  /** Úprava dat objektu (admin: pojmenování rozvaděče / přesun Point `location`). */
  updateObjectData(
    id: string,
    patch: { name?: string; location?: GeoLocation },
  ): Observable<Object> {
    return this.http
      .put<{ success: boolean; data: Object }>(`${this.apiUrl}/${id}`, patch)
      .pipe(
        map((res) => {
          if (res?.success && res.data) return res.data;
          throw new Error('Update failed');
        }),
        catchError(this.handleError),
      );
  }

  /**
   * Get objects by type
   */
  /**
   * Objekty daného typu v polygonu (např. ROZVADEC pro výběr „Zafouknuto z“ u připojovaného).
   */
  getObjectsInPolygon(polygonId: string, objectType: string): Observable<Object[]> {
    const params = new HttpParams().set('objectType', objectType);
    return this.http.get<ObjectResponse>(`${this.apiUrl}/in-polygon/${polygonId}`, { params }).pipe(
      map((response) => {
        if (Array.isArray(response)) {
          return response as Object[];
        }
        if (response.success && Array.isArray(response.data)) {
          return response.data;
        }
        return [];
      }),
      catchError(this.handleError),
    );
  }

  /**
   * Globální KPI pro manažerský dashboard (API vyžaduje roli manager/admin).
   * `userId` = polygony, kde uživatel měnil fáze (záložka Statistiky).
   */
  getManagerDashboardMetrics(
    polygonId?: string,
    responsibleBranch?: string,
    userId?: string,
  ): Observable<ManagerDashboardMetrics> {
    let params = new HttpParams();
    const scopedId = polygonId?.trim();
    if (scopedId) params = params.set('polygonId', scopedId);
    const branchCsv = responsibleBranch?.trim();
    if (branchCsv) params = params.set('responsibleBranch', branchCsv);
    const uid = userId?.trim();
    if (uid) params = params.set('userId', uid);
    return this.http.get<ObjectResponse>(`${this.apiUrl}/manager-dashboard`, { params }).pipe(
      map((response) => {
        if (response.success && response.data && !Array.isArray(response.data)) {
          return response.data as unknown as ManagerDashboardMetrics;
        }
        throw new Error('Failed to load manager dashboard');
      }),
      catchError(this.handleError),
    );
  }

  /** Portfolio v čase (přechody fáze + dotčené polygony), stejné oprávnění jako manager-dashboard. */
  getManagerDashboardTimeseries(params: {
    from: string;
    to: string;
    bucket?: 'day' | 'week' | 'month';
    /** Jedno UUID nebo CSV UUID — jen objekty těchto polygonů. */
    polygonId?: string;
    /** E-mail uživatele — KPI za polygony s jeho aktivitou (Statistiky). */
    userId?: string;
    /** CSV zodpovědných poboček (`polygons.responsible_branch`). */
    responsibleBranch?: string;
    /**
     * `true` = API doplní všechny kalendářní bucket v rozsahu (globální `/dashboard` s M/W/D).
     * Výchozí `false` = jen bucket s aktivitou (polygon dashboard).
     */
    fillBuckets?: boolean;
  }): Observable<ManagerDashboardTimeseriesResult> {
    let hp = new HttpParams().set('from', params.from).set('to', params.to);
    if (params.bucket) hp = hp.set('bucket', params.bucket);
    if (params.polygonId) hp = hp.set('polygonId', params.polygonId);
    if (params.userId?.trim()) hp = hp.set('userId', params.userId.trim());
    if (params.responsibleBranch) hp = hp.set('responsibleBranch', params.responsibleBranch);
    if (params.fillBuckets === true) hp = hp.set('fillBuckets', 'true');
    return this.http.get<ObjectResponse>(`${this.apiUrl}/manager-dashboard/timeseries`, { params: hp }).pipe(
      map((response) => {
        if (response.success && response.data && !Array.isArray(response.data)) {
          return response.data as unknown as ManagerDashboardTimeseriesResult;
        }
        throw new Error('Failed to load manager dashboard timeseries');
      }),
      catchError(this.handleError),
    );
  }

  /**
   * Object ids linked to a path advance batch (`object_history.path_advance_batch_id`) — map batch search.
   */
  /**
   * Poslední upravená dávka trasy pro segment (`object_history.path_advance_batch_id`).
   */
  getLastPathAdvanceBatch(objectId: string): Observable<{
    id: string;
    segmentIds: string[];
    /** Union of segments across all path-advance batches touching the latest batch. */
    allRouteSegmentIds: string[];
    updatedAt?: string;
  } | null> {
    const key = objectId.trim();
    const inflight = this.lastPathAdvanceBatchInflight.get(key);
    if (inflight) return inflight;

    const enc = encodeURIComponent(key);
    const req = this.http
      .get<{
        success: boolean;
        data?: {
          batch?: {
            id: string;
            segmentIds?: string[];
            allRouteSegmentIds?: string[];
            updatedAt?: string;
          } | null;
        };
      }>(`${this.apiUrl}/${enc}/last-path-advance-batch`)
      .pipe(
        map((res) => {
          const batch = res?.success ? res.data?.batch : null;
          if (!batch?.id) return null;
          const segmentIds = Array.isArray(batch.segmentIds) ? batch.segmentIds : [];
          const expanded = Array.isArray(batch.allRouteSegmentIds) ? batch.allRouteSegmentIds : [];
          return {
            id: batch.id,
            segmentIds,
            allRouteSegmentIds: expanded.length > 0 ? expanded : segmentIds,
            updatedAt: batch.updatedAt,
          };
        }),
        catchError(() => of(null)),
        share(),
        finalize(() => this.lastPathAdvanceBatchInflight.delete(key)),
      );
    this.lastPathAdvanceBatchInflight.set(key, req);
    return req;
  }

  findObjectIdsByPathAdvanceBatch(batchId: string): Observable<string[]> {
    const encoded = encodeURIComponent(batchId.trim());
    return this.http.get<ObjectResponse>(`${this.apiUrl}/search/path-advance-batch/${encoded}`).pipe(
      map((response) => {
        if (!response?.success || !response.data || Array.isArray(response.data)) return [];
        const data = response.data as { objectIds?: string[] };
        return Array.isArray(data.objectIds) ? data.objectIds : [];
      }),
      catchError(() => of([])),
    );
  }

  /** VYKOP tab — one row per excavation surface, grouped by path-advance batch + street. */
  getVykopSurfaceRows(params: {
    status?: string;
    polygonId?: string;
    variant?: string;
    search?: string;
    minDate?: string;
    maxDate?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    limit?: number;
    offset?: number;
    activePolygonOnly?: boolean;
  }): Observable<{ rows: import('@core/shared-bridge').VykopSurfaceListRow[]; total: number }> {
    let hp = new HttpParams();
    if (params.status) hp = hp.set('status', params.status);
    if (params.polygonId) hp = hp.set('polygonId', params.polygonId);
    if (params.activePolygonOnly === true) hp = hp.set('activePolygonOnly', 'true');
    if (params.variant) hp = hp.set('variant', params.variant);
    if (params.search) hp = hp.set('search', params.search);
    if (params.minDate) hp = hp.set('minDate', params.minDate);
    if (params.maxDate) hp = hp.set('maxDate', params.maxDate);
    if (params.sortBy) hp = hp.set('sortBy', params.sortBy);
    if (params.sortOrder) hp = hp.set('sortOrder', params.sortOrder);
    if (params.limit != null) hp = hp.set('limit', String(params.limit));
    if (params.offset != null) hp = hp.set('offset', String(params.offset));
    return this.http
      .get<ObjectResponse>(`${this.apiUrl}/vykop-surfaces`, { params: hp })
      .pipe(
        map((response) => {
          if (!response?.success || !response.data || Array.isArray(response.data)) {
            return { rows: [], total: 0 };
          }
          const data = response.data as { rows?: unknown[]; total?: number };
          return {
            rows: Array.isArray(data.rows)
              ? (data.rows as import('@core/shared-bridge').VykopSurfaceListRow[])
              : [],
            total: typeof data.total === 'number' ? data.total : 0,
          };
        }),
        catchError(this.handleError),
      );
  }

  getPathAdvanceBatchDetail(
    batchId: string,
  ): Observable<import('@core/shared-bridge').PathAdvanceBatchDetail | null> {
    const encoded = encodeURIComponent(batchId.trim());
    return this.http
      .get<ObjectResponse>(`${this.apiUrl}/path-advance-batch/${encoded}/detail`)
      .pipe(
        map((response) => {
          if (!response?.success || !response.data || Array.isArray(response.data)) return null;
          const data = response.data as { batch?: import('@core/shared-bridge').PathAdvanceBatchDetail | null };
          return data.batch ?? null;
        }),
        catchError(() => of(null)),
      );
  }

  /**
   * Get component catalog tree for material picker (categories -> components).
   * Default scope = active (zařazené); pass `all` for full catalog.
   * `source: 'product-category'` = strom material_product_category (Obecné + kořeny podle typu objektu).
   * `objectType` = jen větev kořene (mapa).
   */
  getComponentsTree(
    scope: 'active' | 'all' = 'active',
    opts?: { source?: 'legacy' | 'product-category'; objectType?: string },
  ): Observable<TreeNode[]> {
    let params = new HttpParams();
    if (scope === 'all') params = params.set('scope', 'all');
    if (opts?.source === 'product-category') {
      params = params.set('source', 'product-category');
    }
    if (opts?.objectType?.trim()) {
      params = params.set('objectType', opts.objectType.trim());
    }
    return this.http
      .get<{ success: boolean; data?: TreeNode[] }>(`${this.apiUrl}/components/tree`, { params })
      .pipe(
        map((response) => {
          if (response.success && Array.isArray(response.data)) {
            return response.data;
          }
          this.logger.warn('Unexpected components tree response', response);
          return [];
        }),
        catchError(this.handleError),
      );
  }

  /** Flat seznam kategorií produktů (parent_id strom). */
  listProductCategories(): Observable<import('@core/shared-bridge').MaterialProductCategoryDto[]> {
    return this.http
      .get<{
        success: boolean;
        data?: { items?: import('@core/shared-bridge').MaterialProductCategoryDto[] };
      }>(`${this.apiUrl}/product-categories`)
      .pipe(
        map((r) => (r.success && Array.isArray(r.data?.items) ? r.data!.items! : [])),
        catchError(this.handleError),
      );
  }

  getProductCategoryTree(opts?: {
    objectType?: string;
    scope?: 'active' | 'all';
    includeItems?: boolean;
  }): Observable<TreeNode[]> {
    let params = new HttpParams();
    if (opts?.objectType?.trim()) params = params.set('objectType', opts.objectType.trim());
    if (opts?.scope === 'all') params = params.set('scope', 'all');
    if (opts?.includeItems === false) params = params.set('includeItems', 'false');
    return this.http
      .get<{ success: boolean; data?: TreeNode[] }>(`${this.apiUrl}/product-categories/tree`, {
        params,
      })
      .pipe(
        map((response) => (response.success && Array.isArray(response.data) ? response.data : [])),
        catchError(this.handleError),
      );
  }

  createProductCategory(input: {
    name: string;
    parentId: string;
    sortOrder?: number;
  }): Observable<import('@core/shared-bridge').MaterialProductCategoryDto> {
    return this.http
      .post<{
        success: boolean;
        data?: import('@core/shared-bridge').MaterialProductCategoryDto;
      }>(`${this.apiUrl}/product-categories`, input)
      .pipe(
        map((r) => {
          if (!r.success || !r.data) throw new Error('Vytvoření kategorie selhalo');
          return r.data;
        }),
        catchError(this.handleError),
      );
  }

  updateProductCategory(
    id: string,
    body: { name?: string; parentId?: string | null; sortOrder?: number },
  ): Observable<import('@core/shared-bridge').MaterialProductCategoryDto> {
    return this.http
      .patch<{
        success: boolean;
        data?: import('@core/shared-bridge').MaterialProductCategoryDto;
      }>(`${this.apiUrl}/product-categories/${encodeURIComponent(id)}`, body)
      .pipe(
        map((r) => {
          if (!r.success || !r.data) throw new Error('Úprava kategorie selhala');
          return r.data;
        }),
        catchError(this.handleError),
      );
  }

  deleteProductCategory(id: string): Observable<void> {
    return this.http
      .delete<{ success: boolean }>(`${this.apiUrl}/product-categories/${encodeURIComponent(id)}`)
      .pipe(
        map((r) => {
          if (!r.success) throw new Error('Smazání kategorie selhalo');
        }),
        catchError(this.handleError),
      );
  }

  /** Flat katalog komponent (správa aktivních / plný seznam). */
  listComponentsCatalog(opts?: {
    scope?: 'active' | 'all';
    search?: string;
    limit?: number;
  }): Observable<import('@core/shared-bridge').ComponentCatalogItemDto[]> {
    let params = new HttpParams();
    if (opts?.scope === 'all') params = params.set('scope', 'all');
    if (opts?.search?.trim()) params = params.set('search', opts.search.trim());
    if (opts?.limit != null) params = params.set('limit', String(opts.limit));
    return this.http
      .get<{
        success: boolean;
        data?: import('@core/shared-bridge').ComponentCatalogItemDto[];
      }>(`${this.apiUrl}/components`, { params })
      .pipe(
        map((response) => (response.success && Array.isArray(response.data) ? response.data : [])),
        catchError(this.handleError),
      );
  }

  /** Vytvoří nový produkt (SAP) — aktivní výchozí. */
  createComponent(input: {
    sap: string;
    zdrojovyNazev?: string | null;
    jednotka?: string | null;
    categoryId?: string | null;
    isActive?: boolean;
  }): Observable<import('@core/shared-bridge').ComponentCatalogItemDto> {
    return this.http
      .post<{ success: boolean; data?: import('@core/shared-bridge').ComponentCatalogItemDto }>(
        `${this.apiUrl}/components`,
        input,
      )
      .pipe(
        map((r) => {
          if (!r.success || !r.data) throw new Error('Vytvoření produktu selhalo');
          return r.data;
        }),
        catchError(this.handleError),
      );
  }

  activateComponent(sap: string): Observable<import('@core/shared-bridge').ComponentCatalogItemDto> {
    const enc = encodeURIComponent(sap.trim());
    return this.http
      .post<{ success: boolean; data?: import('@core/shared-bridge').ComponentCatalogItemDto }>(
        `${this.apiUrl}/components/${enc}/activate`,
        {},
      )
      .pipe(
        map((r) => {
          if (!r.success || !r.data) throw new Error('Aktivace komponenty selhala');
          return r.data;
        }),
        catchError(this.handleError),
      );
  }

  deactivateComponent(sap: string): Observable<import('@core/shared-bridge').ComponentCatalogItemDto> {
    const enc = encodeURIComponent(sap.trim());
    return this.http
      .post<{ success: boolean; data?: import('@core/shared-bridge').ComponentCatalogItemDto }>(
        `${this.apiUrl}/components/${enc}/deactivate`,
        {},
      )
      .pipe(
        map((r) => {
          if (!r.success || !r.data) throw new Error('Deaktivace komponenty selhala');
          return r.data;
        }),
        catchError(this.handleError),
      );
  }

  updateComponentCatalog(
    sap: string,
    body: {
      isActive?: boolean;
      categoryId?: string | null;
      hlavniKategorie?: string | null;
      subkategorie?: string | null;
      rozvetveni1?: string | null;
      rozvetveni2?: string | null;
      rozvetveni3?: string | null;
      rozvetveni4?: string | null;
      zdrojovyNazev?: string | null;
      jednotka?: string | null;
    },
  ): Observable<import('@core/shared-bridge').ComponentCatalogItemDto> {
    const enc = encodeURIComponent(sap.trim());
    return this.http
      .patch<{ success: boolean; data?: import('@core/shared-bridge').ComponentCatalogItemDto }>(
        `${this.apiUrl}/components/${enc}`,
        body,
      )
      .pipe(
        map((r) => {
          if (!r.success || !r.data) throw new Error('Úprava komponenty selhala');
          return r.data;
        }),
        catchError(this.handleError),
      );
  }

  /** Stáhne aktivní katalog jako .xlsx (blob). */
  exportActiveComponentsCatalog(): Observable<Blob> {
    return this.http
      .get(`${this.apiUrl}/components/export-active`, {
        responseType: 'blob',
      })
      .pipe(
        switchMap((blob) => this.validateCatalogXlsxBlob(blob, 'Export aktivního katalogu')),
        catchError((error) => this.handleCatalogBlobError(error, 'Export aktivního katalogu')),
      );
  }

  /** Export doporučeného katalogu (1 soubor = 1 typ objektu). */
  exportRecommendedComponentsCatalog(objectType: string): Observable<Blob> {
    return this.http
      .get(`${this.apiUrl}/components/export-recommended`, {
        params: { objectType },
        responseType: 'blob',
      })
      .pipe(
        switchMap((blob) => this.validateCatalogXlsxBlob(blob, 'Export doporučeného katalogu')),
        catchError((error) => this.handleCatalogBlobError(error, 'Export doporučeného katalogu')),
      );
  }

  private validateCatalogXlsxBlob(blob: Blob, label: string): Observable<Blob> {
    if (!blob || blob.size === 0) {
      return throwError(() => new Error(`${label} vrátil prázdný soubor`));
    }
    if (blob.type && blob.type.includes('json')) {
      return from(blob.text()).pipe(
        switchMap((text) => {
          let message = `${label} selhal`;
          try {
            const parsed = JSON.parse(text) as { message?: string };
            if (typeof parsed?.message === 'string' && parsed.message.trim()) {
              message = parsed.message.trim();
            }
          } catch {
            /* keep default */
          }
          return throwError(() => new Error(message));
        }),
      );
    }
    return from(blob.slice(0, 2).arrayBuffer()).pipe(
      switchMap((head) => {
        const bytes = new Uint8Array(head);
        if (bytes.length >= 2 && bytes[0] === 0x50 && bytes[1] === 0x4b) {
          return of(blob);
        }
        return throwError(() => new Error(`${label} nevrátil platný Excel soubor`));
      }),
    );
  }

  private handleCatalogBlobError(error: unknown, label: string): Observable<never> {
    const err = error as { error?: Blob; message?: string };
    if (err?.error instanceof Blob) {
      return from(err.error.text()).pipe(
        switchMap((text) => {
          let message =
            typeof err.message === 'string' && err.message.trim() ? err.message : `${label} selhal`;
          try {
            const parsed = JSON.parse(text) as { message?: string };
            if (typeof parsed?.message === 'string' && parsed.message.trim()) {
              message = parsed.message.trim();
            }
          } catch {
            /* keep */
          }
          if (message.includes('500 OK') || message.startsWith('Http failure response')) {
            message = `${label} selhal (server nedostupný nebo vnitřní chyba)`;
          }
          return throwError(() => new Error(message));
        }),
      );
    }
    return this.handleError(error);
  }

  /** Import Excel/CSV aktivního katalogu (hard sync Obecný). */
  importActiveComponentsCatalog(body: {
    fileBase64: string;
    fileName?: string;
  }): Observable<import('@core/shared-bridge').ComponentCatalogImportResult> {
    return this.http
      .post<{
        success: boolean;
        data?: import('@core/shared-bridge').ComponentCatalogImportResult;
        message?: string;
      }>(`${this.apiUrl}/components/import-active`, body)
      .pipe(
        map((r) => {
          if (!r.success || !r.data) throw new Error('Import aktivního katalogu selhal');
          return r.data;
        }),
        catchError(this.handleError),
      );
  }

  /** Import doporučeného katalogu (soft sync, 1 soubor = 1 typ). */
  importRecommendedComponentsCatalog(body: {
    objectType: string;
    fileBase64: string;
    fileName?: string;
  }): Observable<import('@core/shared-bridge').ComponentCatalogImportResult> {
    return this.http
      .post<{
        success: boolean;
        data?: import('@core/shared-bridge').ComponentCatalogImportResult;
        message?: string;
      }>(`${this.apiUrl}/components/import-recommended`, body)
      .pipe(
        map((r) => {
          if (!r.success || !r.data) throw new Error('Import doporučeného katalogu selhal');
          return r.data;
        }),
        catchError(this.handleError),
      );
  }

  /** Doporučený katalog pro typ + fázi (včetně seznamu listů). */
  listRecommendedComponentsCatalog(params: {
    objectType: string;
    phaseKey?: string;
  }): Observable<{
    objectType: string;
    phaseKey: string;
    phaseLabel: string;
    categories: Array<{
      id: string;
      name: string;
      parentId: string | null;
      objectTypeKey: string;
      phaseKey: string;
      sortOrder: number;
    }>;
    items: import('@core/shared-bridge').ComponentCatalogItemDto[];
    sheets: Array<{ key: string; label: string }>;
  }> {
    const httpParams: Record<string, string> = { objectType: params.objectType };
    if (params.phaseKey) httpParams['phaseKey'] = params.phaseKey;
    return this.http
      .get<{
        success: boolean;
        data?: {
          objectType: string;
          phaseKey: string;
          phaseLabel: string;
          categories: Array<{
            id: string;
            name: string;
            parentId: string | null;
            objectTypeKey: string;
            phaseKey: string;
            sortOrder: number;
          }>;
          items: import('@core/shared-bridge').ComponentCatalogItemDto[];
          sheets: Array<{ key: string; label: string }>;
        };
        message?: string;
      }>(`${this.apiUrl}/components/recommended`, { params: httpParams })
      .pipe(
        map((r) => {
          if (!r.success || !r.data) throw new Error('Načtení doporučeného katalogu selhalo');
          return r.data;
        }),
        catchError(this.handleError),
      );
  }

  /** Doporučená zařazení jednoho SAP (typ × fáze). */
  listRecommendedPlacements(sap: string): Observable<{
    sap: string;
    placements: Array<{
      objectType: string;
      phaseKey: string;
      phaseLabel: string;
      categoryId: string | null;
      pathLabel: string | null;
      sortOrder: number;
    }>;
  }> {
    const enc = encodeURIComponent(sap.trim());
    return this.http
      .get<{
        success: boolean;
        data?: {
          sap: string;
          placements: Array<{
            objectType: string;
            phaseKey: string;
            phaseLabel: string;
            categoryId: string | null;
            pathLabel: string | null;
            sortOrder: number;
          }>;
        };
        message?: string;
      }>(`${this.apiUrl}/components/${enc}/recommended-placements`)
      .pipe(
        map((r) => {
          if (!r.success || !r.data) throw new Error('Načtení doporučených zařazení selhalo');
          return r.data;
        }),
        catchError(this.handleError),
      );
  }

  /**
   * Upsert / odebrání doporučeného zařazení SAP.
   * Jedna položka `{ objectType, phaseKey, categoryId }` nebo hromadně `placements[]`.
   * `categoryId` null/prázdné = odebrat z katalogu pro danou fázi.
   */
  upsertRecommendedPlacements(
    sap: string,
    body:
      | {
          objectType: string;
          phaseKey: string;
          categoryId?: string | null;
        }
      | {
          placements: Array<{
            objectType: string;
            phaseKey: string;
            categoryId?: string | null;
          }>;
        },
  ): Observable<unknown> {
    const enc = encodeURIComponent(sap.trim());
    return this.http
      .patch<{ success: boolean; data?: unknown; message?: string }>(
        `${this.apiUrl}/components/${enc}/recommended-placements`,
        body,
      )
      .pipe(
        map((r) => {
          if (!r.success) throw new Error(r.message || 'Uložení doporučeného zařazení selhalo');
          return r.data;
        }),
        catchError(this.handleError),
      );
  }

  /**
   * Komponenta z katalogu podle SAP (název, zařazení, cena z ceníku).
   */
  getComponentBySap(
    sap: string,
  ): Observable<import('@core/shared-bridge').ComponentCatalogItemDto | null> {
    const enc = encodeURIComponent(sap.trim());
    return this.http
      .get<{
        success: boolean;
        data?: import('@core/shared-bridge').ComponentCatalogItemDto & {
          zdrojovy_nazev?: string | null;
        };
      }>(`${this.apiUrl}/components/by-sap/${enc}`)
      .pipe(
        map((response) => {
          const data = response.success ? response.data : null;
          if (!data) return null;
          const zdrojovyNazev = data.zdrojovyNazev ?? data.zdrojovy_nazev ?? null;
          return { ...data, zdrojovyNazev };
        }),
        catchError(() => of(null)),
      );
  }

  /** URL obrázku produktu (stejný pattern jako fotky objektů). */
  getComponentImageUrl(sap: string): string {
    const enc = encodeURIComponent(sap.trim());
    return `${this.apiUrl}/components/${enc}/image`;
  }

  uploadComponentImage(
    sap: string,
    imageDataUrl: string,
  ): Observable<import('@core/shared-bridge').ComponentCatalogItemDto> {
    const enc = encodeURIComponent(sap.trim());
    return this.http
      .post<{
        success: boolean;
        data?: import('@core/shared-bridge').ComponentCatalogItemDto;
      }>(`${this.apiUrl}/components/${enc}/image`, { image: imageDataUrl })
      .pipe(
        map((r) => {
          if (!r.success || !r.data) throw new Error('Nahrání obrázku produktu selhalo');
          return r.data;
        }),
        catchError(this.handleError),
      );
  }

  deleteComponentImage(
    sap: string,
  ): Observable<import('@core/shared-bridge').ComponentCatalogItemDto> {
    const enc = encodeURIComponent(sap.trim());
    return this.http
      .delete<{
        success: boolean;
        data?: import('@core/shared-bridge').ComponentCatalogItemDto;
      }>(`${this.apiUrl}/components/${enc}/image`)
      .pipe(
        map((r) => {
          if (!r.success || !r.data) throw new Error('Smazání obrázku produktu selhalo');
          return r.data;
        }),
        catchError(this.handleError),
      );
  }

  // ===========================================================================
  // PŘIPOJOVANÝ OBJEKT SPEC
  // ===========================================================================
  //
  // `GET /api/objects/:id/spec` (`getPripojovanyObjektSpec`) byl odstraněn.
  // Spec je teď inline v `getObjectById` response pod klíčem `spec`.
  // Samostatný refetch v popupu přepisoval uncommitted změny a kazil diff commit při změně fáze.

  /**
   * Create a point object on the map. Used when user right-clicks on map.
   * Terén (stavba/montáž/záfuk/optika) zakládá SPOJKOVISTE/JAMA/INFO;
   * admin/superadmin navíc BUDOVA/ROZVADEC. Menu filtruje `getCreatablePointTypesForUser`.
   * KONCOVYBOD se nezakládá — bod záfuku žije na spojkovišti.
   */
  createMapObject(params: { type: 'SPOJKOVISTE' | 'JAMA' | 'INFO' | 'BUDOVA' | 'ROZVADEC'; lng: number; lat: number; description?: string }): Observable<Object> {
    const body = {
      type: params.type,
      name: params.type, // Backend overwrites with generated id
      location: { type: 'Point' as const, coordinates: [params.lng, params.lat] },
      ...(params.description != null && params.description !== '' ? { description: params.description } : {})
    };
    return this.http.post<ObjectResponse>(this.apiUrl, body)
      .pipe(
        map(response => {
          if (response.success && response.data && !Array.isArray(response.data)) {
            return response.data as Object;
          }
          throw new Error('Create object failed');
        }),
        catchError(this.handleError)
      );
  }

  /** Kandidáti na spolupráci u výkazu (e-mail + volitelné jméno + role z účtu). */
  getWorkShareCandidates(objectId: string): Observable<{
    emails: string[];
    candidates?: Array<{ email: string; name?: string | null; roles?: string[] }>;
  }> {
    return this.http
      .get<{
        success: boolean;
        data?: {
          emails?: string[];
          candidates?: Array<{ email?: string; name?: string | null; roles?: string[] }>;
        };
      }>(`${this.apiUrl}/${objectId}/work-share/candidates`)
      .pipe(
        map((res) => {
          if (res?.success && res.data) {
            const emails = Array.isArray(res.data.emails) ? res.data.emails : [];
            const candidates = Array.isArray(res.data.candidates)
              ? res.data.candidates
                  .filter((c): c is { email: string; name?: string | null; roles?: string[] } =>
                    typeof c?.email === 'string' && c.email.trim().length > 0
                  )
                  .map((c) => ({ email: c.email.trim(), name: c.name, roles: c.roles }))
              : undefined;
            return { emails, candidates };
          }
          return { emails: [] };
        }),
        catchError(this.handleError)
      );
  }

  /** Uložení spolupracovníků pro rozdělení budoucích zápisů ve výkazu (metadata.workShareCollaboratorEmails). */
  updateWorkShare(objectId: string, collaboratorEmails: string[]): Observable<Object> {
    return this.http
      .put<ObjectResponse>(`${this.apiUrl}/${objectId}/work-share`, { collaboratorEmails })
      .pipe(
        map((response) => {
          if (response.success && response.data && !Array.isArray(response.data)) {
            return response.data as Object;
          }
          throw new Error('Update work share failed');
        }),
        catchError(this.handleError)
      );
  }

  /**
   * Find objects within bounding box
   * @param drawingType Optional drawing type filter ('1' for výkop, '2' for instalace) - filters by multiple object types
   * @param showUnassigned Whether to include UNASSIGNED objects (default: false - maps never show them)
   */
  findObjectsInBbox(minLng: number, minLat: number, maxLng: number, maxLat: number, objectType?: string, drawingType?: string, showUnassigned: boolean = false): Observable<Object[]> {
    let params = new HttpParams()
      .set('minLng', minLng.toString())
      .set('minLat', minLat.toString())
      .set('maxLng', maxLng.toString())
      .set('maxLat', maxLat.toString());
    
    // Drawing type filter takes precedence (filters by multiple types)
    if (drawingType) {
      params = params.set('drawingType', drawingType);
    } else if (objectType) {
      params = params.set('objectType', objectType);
    }

    // Maps never show UNASSIGNED objects (always false)
    params = params.set('showUnassigned', showUnassigned.toString());

    return this.http.get<ObjectResponse>(`${this.apiUrl}/search/bbox`, { params })
      .pipe(
        map(response => {
          if (response.success) {
            // Backend returns { success: true, data: { data: objects[], total: number } }
            if (response.data && typeof response.data === 'object' && 'data' in response.data && Array.isArray(response.data['data'])) {
              return response.data['data'];
            }
            // Fallback for different response formats
            if (Array.isArray(response.data)) {
            return response.data;
            }
          }
          return [];
        }),
        catchError(this.handleError)
      );
  }

  /**
   * Get address points (čísla popisná) in bbox for map layer. Coordinates in WGS84.
   */
  getAddressPointsInBbox(
    minLng: number,
    minLat: number,
    maxLng: number,
    maxLat: number,
    polygonId?: string
  ): Observable<AddressPointMap[]> {
    let params = new HttpParams()
      .set('minLng', minLng.toString())
      .set('minLat', minLat.toString())
      .set('maxLng', maxLng.toString())
      .set('maxLat', maxLat.toString())
      .set('source', 'live'); // Fetch live from ČÚZK API, not from database
    if (polygonId) {
      params = params.set('polygonId', polygonId);
    }
    return this.http.get<ObjectResponse>(`${this.apiUrl}/address-points`, { params }).pipe(
      map(response => {
        if (response?.success && Array.isArray(response.data)) {
          return response.data as unknown as AddressPointMap[];
        }
        return [];
      }),
      catchError(this.handleError)
    );
  }

  /**
   * Import address points from ČÚZK for a polygon
   */
  importAddressPoints(polygonId: string): Observable<{ imported: number; deleted: number; message?: string }> {
    return this.http.post<any>(`${this.apiUrl}/polygon/${polygonId}/address-points/import`, {})
      .pipe(
        map(response => response?.success && response?.data ? response.data : { imported: 0, deleted: 0 }),
        catchError(this.handleError)
      );
  }

  /**
   * Resolve path (points) to segment IDs for route panel. Does not change phase.
   */
  resolvePath(
    points: [number, number][],
    polygonId: string,
    type: string,
    anchorObjectId?: string,
  ): Observable<ResolvePathResponse> {
    // anchorObjectId zarovnává backend kandidáty na stejný status jako kotva (frontend
    // už takto filtruje viditelný výběr v `enterSegmentSelectionMode`). Bez něj backend
    // může u sdílených hran "vyhrát" segment s jiným statusem → špatný lead v popupu.
    const body: { points: [number, number][]; polygonId: string; type: string; anchorObjectId?: string } = {
      points,
      polygonId,
      type,
    };
    if (anchorObjectId) body.anchorObjectId = anchorObjectId;
    return this.http
      .post<{ success: boolean; data?: ResolvePathResponse }>(`${this.apiUrl}/resolve-path`, body)
      .pipe(
        map((res): ResolvePathResponse => (res.success && res.data ? res.data : { segmentIds: [] })),
        catchError(this.handleError),
      );
  }

  /**
   * Get aggregated route content (photos deduplicated by batch) for segment IDs.
   */
  getRouteContent(segmentIds: string[]): Observable<{
    photos: { id: string; objectId: string; url: string; uploadedBy: string | null; uploadedAt?: string | null; statusPhase: string | null }[];
  }> {
    const q = segmentIds.length ? `?segmentIds=${segmentIds.join(',')}` : '';
    return this.http
      .get<{ success: boolean; data?: { photos: any[] } }>(`${this.apiUrl}/route-content${q}`)
      .pipe(
        map((res) => (res.success && res.data ? res.data : { photos: [] })),
        catchError(this.handleError),
      );
  }

  /**
   * Attach one photo to all segments on the route (reference only). Does not change phase.
   */
  attachPhotoToPath(
    points: [number, number][],
    polygonId: string,
    type: string,
    pathAdvanceBatchId: string,
    photo: string,
    options?: { description?: string; category?: string; photoType?: string; statusPhase?: string },
  ): Observable<{ photoId: string; url: string }> {
    return this.http
      .post<{ success: boolean; data?: { photoId: string; url: string } }>(`${this.apiUrl}/attach-to-path`, {
        points,
        polygonId,
        type,
        pathAdvanceBatchId,
        photo,
        ...options,
      })
      .pipe(
        map((res) => {
          if (res.success && res.data) return res.data;
          throw new Error('attach-to-path failed');
        }),
        catchError(this.handleError),
      );
  }

  /**
   * Get object type definitions
   */
  getObjectTypeDefinitions(): Observable<ObjectTypeDefinition[]> {
    return this.http.get<ObjectResponse>(`${this.apiUrl}/types`)
      .pipe(
        map(response => {
          if (response.success && Array.isArray(response.data)) {
            return response.data as unknown as ObjectTypeDefinition[];
          }
          return this.getDefaultObjectTypes();
        }),
        catchError(this.handleError)
      );
  }

  /**
   * Get materials used globally (for Materiály tab) — paginated; optional facets for filter dropdowns.
   */
  getMaterialsUsed(paramsIn: MaterialsUsedQueryParams): Observable<MaterialsUsedPageResponse> {
    let params = new HttpParams();
    const hasDateBounds = !!(paramsIn.dateFrom?.trim() || paramsIn.dateTo?.trim());
    if (paramsIn.months != null && !hasDateBounds) {
      const months = Math.min(120, Math.max(1, paramsIn.months));
      params = params.set('months', String(months));
    }
    if (paramsIn.month) params = params.set('month', paramsIn.month);
    if (paramsIn.dateFrom) params = params.set('dateFrom', paramsIn.dateFrom);
    if (paramsIn.dateTo) params = params.set('dateTo', paramsIn.dateTo);
    if (paramsIn.limit != null) params = params.set('limit', String(paramsIn.limit));
    if (paramsIn.offset != null) params = params.set('offset', String(paramsIn.offset));
    if (paramsIn.changedBy) params = params.set('changedBy', paramsIn.changedBy);
    if (paramsIn.polygonId) params = params.set('polygonId', paramsIn.polygonId);
    if (paramsIn.mainCategory) params = params.set('mainCategory', paramsIn.mainCategory);
    if (paramsIn.objectType) params = params.set('objectType', paramsIn.objectType);
    if (paramsIn.objectStatus) params = params.set('objectStatus', paramsIn.objectStatus);
    if (paramsIn.includeFacets === true) params = params.set('includeFacets', 'true');
    if (paramsIn.facetPhaseForType) params = params.set('facetPhaseForType', paramsIn.facetPhaseForType);
    if (paramsIn.activePolygonOnly === true) params = params.set('activePolygonOnly', 'true');
    if (paramsIn.stavbaPolygonOnly === true) params = params.set('stavbaPolygonOnly', 'true');
    return this.http.get<any>(`${this.apiUrl}/materials-used`, { params }).pipe(
      map((response) => {
        if (response?.success && response?.data) {
          const d = response.data;
          return {
            items: Array.isArray(d.items) ? d.items : [],
            totalCount: Number(d.totalCount) || 0,
            aggregateLineTotal: Number(d.aggregateLineTotal) || 0,
            facets: d.facets,
          } as MaterialsUsedPageResponse;
        }
        return { items: [], totalCount: 0, aggregateLineTotal: 0 };
      }),
      catchError(this.handleError)
    );
  }

  /** Reporting: spotřeba materiálu agregovaná na polygon (oceňování z unit_price). */
  getMaterialConsumptionByPolygon(paramsIn: {
    dateFrom?: string;
    dateTo?: string;
    polygonId?: string;
    months?: number;
  }): Observable<MaterialConsumptionByPolygonReport> {
    let params = new HttpParams();
    if (paramsIn.dateFrom?.trim()) params = params.set('dateFrom', paramsIn.dateFrom.trim());
    if (paramsIn.dateTo?.trim()) params = params.set('dateTo', paramsIn.dateTo.trim());
    if (paramsIn.polygonId?.trim()) params = params.set('polygonId', paramsIn.polygonId.trim());
    if (paramsIn.months != null && Number.isFinite(paramsIn.months)) {
      params = params.set('months', String(paramsIn.months));
    }
    return this.http.get<any>(`${this.apiUrl}/materials/reports/consumption`, { params }).pipe(
      map((response) => {
        if (response?.success && response?.data) {
          const d = response.data;
          return {
            items: Array.isArray(d.items) ? d.items : [],
            totals: {
              quantity: Number(d.totals?.quantity) || 0,
              lineTotal: Number(d.totals?.lineTotal) || 0,
              usageRowCount: Number(d.totals?.usageRowCount) || 0,
            },
          } as MaterialConsumptionByPolygonReport;
        }
        return { items: [], totals: { quantity: 0, lineTotal: 0, usageRowCount: 0 } };
      }),
      catchError(this.handleError),
    );
  }

  /**
   * Get work report for current user (Výkaz práce) – by month
   */
  getWorkReport(
    months: number = 12,
    monthFilter?: string,
    dateFrom?: string,
    dateTo?: string,
    paymentStatus?: WorkReportPaymentStatus[],
  ): Observable<{
    byMonth: Record<string, WorkReportRow[]>;
    totalPrice: number;
  }> {
    let params = new HttpParams().set('months', months.toString());
    if (monthFilter) {
      params = params.set('month', monthFilter);
    }
    if (dateFrom) params = params.set('dateFrom', dateFrom);
    if (dateTo) params = params.set('dateTo', dateTo);
    if (paymentStatus?.length) {
      params = params.set('paymentStatus', paymentStatus.join(','));
    }
    return this.http.get<any>(`${this.apiUrl}/work-report/me`, { params }).pipe(
      map(response => {
        if (response?.success && response?.data) {
          return response.data;
        }
        return { byMonth: {}, totalPrice: 0 };
      }),
      catchError(this.handleError)
    );
  }

  /**
   * Get work report for a specific user. Use month (YYYY-MM) or dateFrom+dateTo (YYYY-MM-DD).
   * Field-only roles may read their own userId; others per canViewOtherUsersActivityAndWorkReport.
   */
  getWorkReportForUser(
    userId: string,
    months: number = 12,
    monthFilter?: string,
    dateFrom?: string,
    dateTo?: string,
    paymentStatus?: WorkReportPaymentStatus[],
  ): Observable<{ byMonth: Record<string, WorkReportRow[]>; totalPrice: number }> {
    let params = new HttpParams().set('months', months.toString());
    if (monthFilter) params = params.set('month', monthFilter);
    if (dateFrom) params = params.set('dateFrom', dateFrom);
    if (dateTo) params = params.set('dateTo', dateTo);
    if (paymentStatus?.length) params = params.set('paymentStatus', paymentStatus.join(','));
    return this.http.get<any>(`${this.apiUrl}/work-report/user/${encodeURIComponent(userId)}`, { params }).pipe(
      map(response => {
        if (response?.success && response?.data) return response.data;
        return { byMonth: {}, totalPrice: 0 };
      }),
      catchError(this.handleError)
    );
  }

  /** Server odmítne export řádků, které volající nesmí číst (stavbyvedoucí jen ve svém rozsahu). */
  assertWorkReportExportAccess(userIds: string[]): Observable<void> {
    return this.http
      .post<{ success?: boolean }>(`${this.apiUrl}/work-report/export-access`, { userIds })
      .pipe(
        map((response) => {
          if (!response?.success) {
            throw new Error('Export výkazu není dostupný.');
          }
        }),
        catchError(this.handleError),
      );
  }

  /**
   * Součty výkazu práce (`payment`/ mzdy orient.) podle osoby — volitelně jen jeden polygon.
   * Oprávnění jako výkaz jiného uživatele (`canViewOtherUsersActivityAndWorkReport`).
   */
  getWorkReportAggregate(
    polygonId?: string,
    dateRange?: {
      dateFrom?: string;
      dateTo?: string;
      activePolygonOnly?: boolean;
      stavbaPolygonOnly?: boolean;
    },
  ): Observable<{
    totalPrice: number;
    byUser: Array<{ userId: string; totalPrice: number; entryCount: number }>;
  }> {
    let params = new HttpParams();
    if (polygonId?.trim()) {
      params = params.set('polygonId', polygonId.trim());
    }
    if (dateRange?.dateFrom?.trim()) {
      params = params.set('dateFrom', dateRange.dateFrom.trim());
    }
    if (dateRange?.dateTo?.trim()) {
      params = params.set('dateTo', dateRange.dateTo.trim());
    }
    if (dateRange?.activePolygonOnly === true) {
      params = params.set('activePolygonOnly', 'true');
    }
    if (dateRange?.stavbaPolygonOnly === true) {
      params = params.set('stavbaPolygonOnly', 'true');
    }
    return this.http.get<any>(`${this.apiUrl}/work-report/aggregate`, { params }).pipe(
      map((response) => {
        if (response?.success && response?.data) {
          const d = response.data;
          return {
            totalPrice: Number(d.totalPrice) || 0,
            byUser: Array.isArray(d.byUser) ? d.byUser : [],
          };
        }
        return { totalPrice: 0, byUser: [] };
      }),
      catchError(this.handleError),
    );
  }

  /**
   * Výdělek firmy za období: smluvní tržba (investor) vs. odměny pracovníků.
   * `month` = YYYY-MM; jinak dateFrom/dateTo (YYYY-MM-DD).
   */
  getCompanyWorkReportEarnings(
    companyId: string,
    options?: { month?: string; dateFrom?: string; dateTo?: string },
  ): Observable<{
    payoutTotal: number;
    investorTotal: number;
    marginTotal: number;
    entryCount: number;
    currency: string;
  }> {
    const empty = {
      payoutTotal: 0,
      investorTotal: 0,
      marginTotal: 0,
      entryCount: 0,
      currency: 'CZK',
    };
    const cid = companyId?.trim();
    if (!cid) return of(empty);
    let params = new HttpParams().set('companyId', cid);
    if (options?.month?.trim()) params = params.set('month', options.month.trim());
    if (options?.dateFrom?.trim()) params = params.set('dateFrom', options.dateFrom.trim());
    if (options?.dateTo?.trim()) params = params.set('dateTo', options.dateTo.trim());
    return this.http.get<any>(`${this.apiUrl}/work-report/company-earnings`, { params }).pipe(
      map((response) => {
        if (response?.success && response?.data) {
          const d = response.data;
          return {
            payoutTotal: Number(d.payoutTotal) || 0,
            investorTotal: Number(d.investorTotal) || 0,
            marginTotal: Number(d.marginTotal) || 0,
            entryCount: Number(d.entryCount) || 0,
            currency: String(d.currency || 'CZK'),
          };
        }
        return empty;
      }),
      catchError(this.handleError),
    );
  }

  /**
   * Výkaz firmy ve stejném tvaru jako `getWorkReport`. Cena na obrazovce je smluvní.
   */
  getCompanyWorkReport(
    companyId: string,
    months: number = 12,
    monthFilter?: string,
    dateFrom?: string,
    dateTo?: string,
    paymentStatus?: WorkReportPaymentStatus[],
  ): Observable<{ byMonth: Record<string, WorkReportRow[]>; totalPrice: number }> {
    const cid = companyId?.trim();
    if (!cid) return of({ byMonth: {}, totalPrice: 0 });
    let params = new HttpParams().set('companyId', cid).set('months', months.toString());
    if (monthFilter) params = params.set('month', monthFilter);
    if (dateFrom) params = params.set('dateFrom', dateFrom);
    if (dateTo) params = params.set('dateTo', dateTo);
    if (paymentStatus?.length) params = params.set('paymentStatus', paymentStatus.join(','));
    return this.http.get<any>(`${this.apiUrl}/work-report/company`, { params }).pipe(
      map((response) => {
        if (response?.success && response?.data) return response.data;
        return { byMonth: {}, totalPrice: 0 };
      }),
      catchError(this.handleError),
    );
  }

  /** Historické faktury firmy. Součet je smluvní částka. */
  getCompanyWorkReportInvoices(companyId: string): Observable<PolygonWorkReportInvoiceSummary[]> {
    const cid = companyId?.trim();
    if (!cid) return of([]);
    const params = new HttpParams().set('companyId', cid);
    return this.http.get<any>(`${this.apiUrl}/work-report/company/invoices`, { params }).pipe(
      map((response) => {
        if (response?.success && response?.data?.invoices && Array.isArray(response.data.invoices)) {
          return response.data.invoices as PolygonWorkReportInvoiceSummary[];
        }
        return [] as PolygonWorkReportInvoiceSummary[];
      }),
      catchError(this.handleError),
    );
  }

  /**
   * Plochá list řádků výkazu pro polygon dashboard – Výkaz práce.
   * Multi-hodnotové filtry (`objectType`, `phaseTo`) BE přijímá jako CSV.
   */
  getPolygonWorkReportRows(
    polygonId: string,
    filters?: {
      dateFrom?: string;
      dateTo?: string;
      userId?: string;
      userIds?: string[];
      objectType?: string[];
      phaseTo?: string[];
      paymentStatus?: WorkReportPaymentStatus[];
    },
  ): Observable<WorkReportRow[]> {
    let params = new HttpParams();
    if (filters?.dateFrom) params = params.set('dateFrom', filters.dateFrom);
    if (filters?.dateTo) params = params.set('dateTo', filters.dateTo);
    const userIds = (filters?.userIds ?? []).filter((s) => !!s?.trim());
    if (userIds.length > 0) params = params.set('userId', userIds.join(','));
    else if (filters?.userId) params = params.set('userId', filters.userId);
    const types = (filters?.objectType ?? []).filter((s) => !!s?.trim());
    if (types.length > 0) params = params.set('objectType', types.join(','));
    const phases = (filters?.phaseTo ?? []).filter((s) => !!s?.trim());
    if (phases.length > 0) params = params.set('phaseTo', phases.join(','));
    if (filters?.paymentStatus?.length) {
      params = params.set('paymentStatus', filters.paymentStatus.join(','));
    }
    return this.http
      .get<any>(`${this.apiUrl}/work-report/polygon/${encodeURIComponent(polygonId)}/rows`, { params })
      .pipe(
        map((response) => {
          if (response?.success && response?.data?.rows && Array.isArray(response.data.rows)) {
            return response.data.rows as WorkReportRow[];
          }
          return [] as WorkReportRow[];
        }),
        catchError(this.handleError),
      );
  }

  /**
   * Souhrn historických faktur v polygonu (skupiny řádků se stejným `invoice_id`).
   */
  getPolygonWorkReportInvoices(polygonId: string): Observable<PolygonWorkReportInvoiceSummary[]> {
    return this.http
      .get<any>(`${this.apiUrl}/work-report/polygon/${encodeURIComponent(polygonId)}/invoices`)
      .pipe(
        map((response) => {
          if (response?.success && response?.data?.invoices && Array.isArray(response.data.invoices)) {
            return response.data.invoices as PolygonWorkReportInvoiceSummary[];
          }
          return [] as PolygonWorkReportInvoiceSummary[];
        }),
        catchError(this.handleError),
      );
  }

  /** Souhrn historických faktur uživatele (napříč polygony). */
  getUserWorkReportInvoices(userId: string): Observable<PolygonWorkReportInvoiceSummary[]> {
    return this.http
      .get<any>(`${this.apiUrl}/work-report/user/${encodeURIComponent(userId)}/invoices`)
      .pipe(
        map((response) => {
          if (response?.success && response?.data?.invoices && Array.isArray(response.data.invoices)) {
            return response.data.invoices as PolygonWorkReportInvoiceSummary[];
          }
          return [] as PolygonWorkReportInvoiceSummary[];
        }),
        catchError(this.handleError),
      );
  }

  /**
   * Globální seznam historických faktur (admin / superadmin — Fakturace).
   * `status`: `all` | `paid` | `unpaid` (alias `pending`).
   * Volitelné filtry odpovídají BE query params (web Fakturace je zatím aplikuje client-side).
   */
  getAllWorkReportInvoices(
    status: 'all' | 'paid' | 'unpaid' | 'pending' = 'all',
    filters?: {
      polygonIds?: string[];
      userIds?: string[];
      dateFrom?: string;
      dateTo?: string;
      q?: string;
    },
  ): Observable<PolygonWorkReportInvoiceSummary[]> {
    let params = new HttpParams();
    if (status && status !== 'all') {
      params = params.set('status', status);
    }
    const polyCsv = (filters?.polygonIds ?? []).map((s) => s.trim()).filter(Boolean).join(',');
    const userCsv = (filters?.userIds ?? []).map((s) => s.trim()).filter(Boolean).join(',');
    if (polyCsv) params = params.set('polygonId', polyCsv);
    if (userCsv) params = params.set('userId', userCsv);
    if (filters?.dateFrom?.trim()) params = params.set('dateFrom', filters.dateFrom.trim());
    if (filters?.dateTo?.trim()) params = params.set('dateTo', filters.dateTo.trim());
    if (filters?.q?.trim()) params = params.set('q', filters.q.trim());
    return this.http.get<any>(`${this.apiUrl}/work-report/invoices`, { params }).pipe(
      map((response) => {
        if (response?.success && response?.data?.invoices && Array.isArray(response.data.invoices)) {
          return response.data.invoices as PolygonWorkReportInvoiceSummary[];
        }
        return [] as PolygonWorkReportInvoiceSummary[];
      }),
      catchError(this.handleError),
    );
  }

  /**
   * Snapshot řádků jedné faktury (zmrazený, nezávislý na aktuálním payment_status).
   */
  getWorkReportInvoiceRows(invoiceId: string): Observable<WorkReportRow[]> {
    return this.http
      .get<any>(`${this.apiUrl}/work-report/invoices/${encodeURIComponent(invoiceId)}/rows`)
      .pipe(
        map((response) => {
          if (response?.success && response?.data?.rows && Array.isArray(response.data.rows)) {
            return response.data.rows as WorkReportRow[];
          }
          return [] as WorkReportRow[];
        }),
        catchError(this.handleError),
      );
  }

  /** Poslední záznamy historie objektů v polygonu (paginované). */
  getPolygonActivityFeed(paramsIn: {
    polygonId: string;
    limit?: number;
    offset?: number;
  }): Observable<{
    items: Array<{
      id: string;
      objectId: string;
      objectName: string;
      objectType: string;
      changeType: string;
      changedBy: string;
      createdAt: string;
      changeDescription: string | null;
      statusTransitionFrom: string | null;
      statusTransitionTo: string | null;
      photoIds?: string[];
      materialUsed?: Array<{ sapCode?: string; quantity?: number; label?: string }>;
      newValues?: Record<string, unknown> | null;
      objectMetadata?: Record<string, unknown> | null;
    }>;
    total: number;
  }> {
    let params = new HttpParams();
    if (paramsIn.limit != null) params = params.set('limit', String(paramsIn.limit));
    if (paramsIn.offset != null) params = params.set('offset', String(paramsIn.offset));
    return this.http
      .get<any>(`${this.apiUrl}/history/polygon/${encodeURIComponent(paramsIn.polygonId)}`, { params })
      .pipe(
        map((response) => {
          if (response?.success && response?.data) {
            const d = response.data;
            return {
              items: Array.isArray(d.items) ? d.items : [],
              total: Number(d.total) || 0,
            };
          }
          return { items: [], total: 0 };
        }),
        catchError(this.handleError),
      );
  }

  /**
   * Create manual work report entry (Přidat záznam).
   * Admin can pass userId to create entry for that user.
   */
  createWorkReportEntry(payload: {
    userId?: string;
    polygonId?: string;
    activityCode?: string;
    quantity?: number;
    activityName?: string;
    description?: string;
    price?: number;
  }): Observable<WorkReportRow> {
    return this.http.post<any>(`${this.apiUrl}/work-report`, payload).pipe(
      map(response => {
        if (response?.success && response?.data) return response.data;
        throw new Error(response?.message || 'Failed to create entry');
      }),
      catchError(this.handleError)
    );
  }

  /**
   * Update work report entry (admin only).
   */
  updateWorkReportEntry(
    id: string,
    payload: {
      activityName?: string;
      description?: string;
      price?: number;
      excavationLengthM?: number;
      lengthM?: number;
      quantity?: number;
      widthM?: number;
      depthM?: number;
      deepeningCm?: number;
      ukonceniVRd?: string;
      ukonceniVRdOther?: string;
      propagateToObject?: boolean;
      createdAt?: string;
    },
  ): Observable<WorkReportRow> {
    return this.http.patch<any>(`${this.apiUrl}/work-report/${id}`, payload).pipe(
      map(response => {
        if (response?.success && response?.data) return response.data;
        throw new Error(response?.message || 'Failed to update entry');
      }),
      catchError(this.handleError)
    );
  }

  /**
   * Delete work report entry (admin only).
   */
  deleteWorkReportEntry(id: string): Observable<void> {
    return this.http.delete<any>(`${this.apiUrl}/work-report/${id}`).pipe(
      map(response => {
        if (!response?.success) throw new Error(response?.message || 'Failed to delete entry');
      }),
      catchError(this.handleError)
    );
  }

  /**
   * Hromadné vrácení vyfakturovaných řádků na neproplacené.
   */
  patchWorkReportPaymentStatus(
    ids: string[],
    paymentStatus: 'unpaid',
  ): Observable<{ updated: number }> {
    return this.http.patch<any>(`${this.apiUrl}/work-report/payment-status`, { ids, paymentStatus }).pipe(
      map((response) => {
        if (response?.success && response?.data) {
          const d = response.data as { updated?: number };
          return { updated: Number(d.updated) || 0 };
        }
        throw new Error(response?.message || 'Failed to update payment status');
      }),
      catchError(this.handleError),
    );
  }

  /** Vytvoření faktury z neproplacených řádků (Označit jako vyfakturované). */
  invoiceWorkReport(ids: string[]): Observable<{ updated: number; invoiceId: string }> {
    return this.http.post<any>(`${this.apiUrl}/work-report/invoice`, { ids }).pipe(
      map((response) => {
        if (response?.success && response?.data) {
          const d = response.data as { updated?: number; invoiceId?: string };
          const invoiceId = String(d.invoiceId ?? '').trim();
          if (!invoiceId) throw new Error('Chybí identifikátor faktury');
          return { updated: Number(d.updated) || 0, invoiceId };
        }
        throw new Error(response?.message || 'Failed to create work report invoice');
      }),
      catchError(this.handleError),
    );
  }

  /** @deprecated Use {@link invoiceWorkReport}. */
  settleWorkReport(ids: string[]): Observable<{ updated: number; invoiceId: string }> {
    return this.invoiceWorkReport(ids);
  }

  /** Admin / superadmin: označit nezaplacenou fakturu jako proplacenou. */
  payWorkReportInvoice(invoiceId: string): Observable<{ updated: number }> {
    return this.http.patch<any>(`${this.apiUrl}/work-report/invoices/${invoiceId}/pay`, {}).pipe(
      map((response) => {
        if (response?.success && response?.data) {
          const d = response.data as { updated?: number };
          return { updated: Number(d.updated) || 0 };
        }
        throw new Error(response?.message || 'Failed to pay invoice');
      }),
      catchError(this.handleError),
    );
  }

  /** Smazání nezaplacené faktury — vrátí řádky na neproplacené. */
  deleteWorkReportInvoice(invoiceId: string): Observable<{ reverted: number }> {
    return this.http.delete<any>(`${this.apiUrl}/work-report/invoices/${invoiceId}`).pipe(
      map((response) => {
        if (response?.success && response?.data) {
          const d = response.data as { reverted?: number };
          return { reverted: Number(d.reverted) || 0 };
        }
        throw new Error(response?.message || 'Failed to delete invoice');
      }),
      catchError(this.handleError),
    );
  }

  /** Admin / superadmin: vrátit proplacenou fakturu na vyfakturované. */
  revertPaidWorkReportInvoice(invoiceId: string): Observable<{ updated: number }> {
    return this.http.patch<any>(`${this.apiUrl}/work-report/invoices/${invoiceId}/revert-paid`, {}).pipe(
      map((response) => {
        if (response?.success && response?.data) {
          const d = response.data as { updated?: number };
          return { updated: Number(d.updated) || 0 };
        }
        throw new Error(response?.message || 'Failed to revert paid invoice');
      }),
      catchError(this.handleError),
    );
  }

  // ────────────────────────────────────────────────────────────────────
  // Akviziční report (vstupní data projektu) — import + editace (Manažer+)
  // ────────────────────────────────────────────────────────────────────

  /** Náhled importu akvizičního reportu (bez zápisu); `rows` = řádky hlavička→hodnota. */
  analyzeAcquisitionReport(
    polygonId: string,
    rows: Array<Record<string, string>>,
  ): Observable<AcquisitionAnalyzeResult> {
    return this.http
      .post<{ success: boolean; data: AcquisitionAnalyzeResult }>(
        `${this.apiUrl}/acquisition/${polygonId}/analyze`,
        { rows },
      )
      .pipe(
        map((res) => res.data),
        catchError(this.handleError),
      );
  }

  /** Import akvizičního reportu (upsert akvizičních dat budov). */
  commitAcquisitionReport(
    polygonId: string,
    rows: Array<Record<string, string>>,
    fileName?: string,
  ): Observable<AcquisitionCommitResult> {
    return this.http
      .post<{ success: boolean; data: AcquisitionCommitResult }>(
        `${this.apiUrl}/acquisition/${polygonId}/commit`,
        { rows, fileName },
      )
      .pipe(
        map((res) => res.data),
        catchError(this.handleError),
      );
  }

  /** Úprava akvizičních dat budovy (Manažer+). */
  updateAcquisitionForObject(
    objectId: string,
    patch: ObjectAcquisitionPatch,
  ): Observable<ObjectAcquisition> {
    return this.http
      .put<{ success: boolean; data: ObjectAcquisition }>(
        `${this.apiUrl}/acquisition/object/${objectId}`,
        patch,
      )
      .pipe(
        map((res) => res.data),
        catchError(this.handleError),
      );
  }

  /** Zamítnutí akvizice (Status ACQ = Nesouhlas) — role akvizice + mutátoři smluv. */
  rejectAcquisitionForObject(objectId: string): Observable<ObjectAcquisition> {
    return this.http
      .put<{ success: boolean; data: ObjectAcquisition }>(
        `${this.apiUrl}/acquisition/object/${objectId}/reject`,
        {},
      )
      .pipe(
        map((res) => res.data),
        catchError((err) => {
          // Live object-service may predate `/reject` (Nest: Cannot PUT …/reject).
          if (err?.status === 404) {
            return this.updateAcquisitionForObject(objectId, {
              statusAcq: ACQUISITION_STATUS.NESOUHLAS,
            });
          }
          return this.handleError(err);
        }),
      );
  }

  /** Uzavření akvizice (Status ACQ = Uzavřeno) — role akvizice + mutátoři smluv. */
  closeAcquisitionForObject(objectId: string): Observable<ObjectAcquisition> {
    return this.http
      .put<{ success: boolean; data: ObjectAcquisition }>(
        `${this.apiUrl}/acquisition/object/${objectId}/close`,
        {},
      )
      .pipe(
        map((res) => res.data),
        catchError((err) => {
          if (err?.status === 404) {
            return this.updateAcquisitionForObject(objectId, {
              statusAcq: ACQUISITION_STATUS.UZAVRENO,
            });
          }
          return this.handleError(err);
        }),
      );
  }

  /** Nespecifikováno (Status ACQ = Zadáno) — role akvizice + mutátoři smluv. */
  unspecifyAcquisitionForObject(objectId: string): Observable<ObjectAcquisition> {
    return this.http
      .put<{ success: boolean; data: ObjectAcquisition }>(
        `${this.apiUrl}/acquisition/object/${objectId}/unspecify`,
        {},
      )
      .pipe(
        map((res) => res.data),
        catchError((err) => {
          if (err?.status === 404) {
            return this.updateAcquisitionForObject(objectId, {
              statusAcq: ACQUISITION_STATUS.ZADANO,
            });
          }
          return this.handleError(err);
        }),
      );
  }

  /**
   * Výkres Akvizice: persist Status ACQ after Podepsat / Zamítnutá (legacy building-type path).
   * Dedicated `/close` and `/reject` 404 on APIs that only have PUT `/acquisition/object/:id`.
   */
  persistAkviziceDrawingStatusAcq(
    objectId: string,
    type: BudovaBuildingType,
  ): Observable<ObjectAcquisition> {
    const statusAcq = akviziceDrawingStatusAcqForBuildingType(type);
    if (!statusAcq) {
      return throwError(() => new Error('Stav akvizice se nepodařilo uložit.'));
    }
    if (type === 'PRIPOJENA') return this.closeAcquisitionForObject(objectId);
    return this.rejectAcquisitionForObject(objectId);
  }

  /** Úprava libovolných sloupců reportu z editovatelné mřížky (Manažer+). */
  updateAcquisitionRawForObject(
    objectId: string,
    raw: Record<string, string>,
  ): Observable<ObjectAcquisition> {
    return this.http
      .put<{ success: boolean; data: ObjectAcquisition }>(
        `${this.apiUrl}/acquisition/object/${objectId}/raw`,
        { raw },
      )
      .pipe(
        map((res) => res.data),
        catchError(this.handleError),
      );
  }

  /** Smazání všech akvizičních dat v polygonu (Manažer+). Vrací počet smazaných záznamů. */
  deleteAcquisitionForPolygon(polygonId: string): Observable<number> {
    return this.http
      .delete<{ success: boolean; data: { deleted: number } }>(
        `${this.apiUrl}/acquisition/${polygonId}`,
      )
      .pipe(
        map((res) => res.data?.deleted ?? 0),
        catchError(this.handleError),
      );
  }

  /** Náhled importu tabulky rozvláknění (bez zápisu); `rows` = řádky hlavička→hodnota. */
  analyzeRozvlakneniReport(
    polygonId: string,
    rows: Array<Record<string, string>>,
  ): Observable<RozvlakneniAnalyzeResult> {
    return this.http
      .post<{ success: boolean; data: RozvlakneniAnalyzeResult }>(
        `${this.apiUrl}/rozvlakneni/${polygonId}/analyze`,
        { rows },
      )
      .pipe(
        map((res) => res.data),
        catchError(this.handleError),
      );
  }

  /** Import tabulky rozvláknění: doplní jen prázdná data existujících budov. */
  commitRozvlakneniReport(
    polygonId: string,
    rows: Array<Record<string, string>>,
    fileName?: string,
  ): Observable<RozvlakneniCommitResult> {
    return this.http
      .post<{ success: boolean; data: RozvlakneniCommitResult }>(
        `${this.apiUrl}/rozvlakneni/${polygonId}/commit`,
        { rows, fileName },
      )
      .pipe(
        map((res) => res.data),
        catchError(this.handleError),
      );
  }

  /** Náhled importu tabulky ROPID (vytvoření/aktualizace BUDOVA). */
  analyzeRopidReport(
    polygonId: string,
    rows: Array<Record<string, string>>,
    importMode?: string,
  ): Observable<RopidAnalyzeResult> {
    return this.http
      .post<{ success: boolean; data: RopidAnalyzeResult }>(
        `${this.apiUrl}/ropid/${polygonId}/analyze`,
        { rows, importMode },
      )
      .pipe(
        map((res) => res.data),
        catchError(this.handleError),
      );
  }

  /** Import tabulky ROPID — vytvoří nebo aktualizuje BUDOVA na souřadnicích S-JTSK. */
  commitRopidReport(
    polygonId: string,
    rows: Array<Record<string, string>>,
    fileName?: string,
    importMode?: string,
  ): Observable<RopidCommitResult> {
    return this.http
      .post<{ success: boolean; data: RopidCommitResult }>(
        `${this.apiUrl}/ropid/${polygonId}/commit`,
        { rows, fileName, importMode },
      )
      .pipe(
        map((res) => res.data),
        catchError(this.handleError),
      );
  }

  /** Unified import — náhled spárování více souborů. */
  analyzeUnifiedImport(
    polygonId: string,
    files: Array<{
      fileName: string;
      sheetName?: string;
      sourceType?: string;
      rows: Array<Record<string, string>>;
    }>,
    importMode?: string,
  ): Observable<UnifiedImportAnalyzeResult> {
    return this.http
      .post<{ success: boolean; data: UnifiedImportAnalyzeResult }>(
        `${this.apiUrl}/unified-import/${polygonId}/analyze`,
        { files, importMode },
      )
      .pipe(map((res) => res.data), catchError(this.handleError));
  }

  /** Unified import — enrich + volitelné vytvoření BUDOVA (bez auto-apply katalogu Úřadů). */
  commitUnifiedImport(
    batchId: string,
    decisions: Array<{ candidateId: string; action: 'create' | 'skip' }>,
    importMode?: string,
    applyAuthorityCatalog: boolean = false,
    polygonId?: string,
  ): Observable<UnifiedImportCommitResult> {
    const apply = applyAuthorityCatalog === true;
    return this.http
      .post<{ success: boolean; data: UnifiedImportCommitResult }>(
        `${this.apiUrl}/unified-import/commit`,
        {
          batchId,
          decisions,
          importMode,
          applyAuthorityCatalog: apply,
          ...(polygonId
            ? { applyAuthorityCatalogByPolygon: buildAuthorityCatalogApplyByPolygon(polygonId, apply) }
            : {}),
        },
      )
      .pipe(map((res) => res.data), catchError(this.handleError));
  }

  // ────────────────────────────────────────────────────────────────────
  // Ceník Protelco + PPD import (superadmin)
  // ────────────────────────────────────────────────────────────────────

  /** Aggregate import flags per polygon (Geometrie, tabulka objektů, PPD, návaznost, vlastníci). */
  getImportPolygonStatuses(): Observable<PolygonImportStatusRow[]> {
    return this.http
      .get<{ success: boolean; data: { statuses: PolygonImportStatusRow[] } }>(
        `${this.apiUrl}/import/polygon-status`,
      )
      .pipe(map((res) => res.data?.statuses ?? []), catchError(this.handleError));
  }

  /** Seznam nahraných / importovaných souborů podle typu importu. */
  getImportUploadedFiles(
    kind: ImportUploadKind,
    polygonId?: string,
  ): Observable<ImportUploadedFileRecord[]> {
    let params = new HttpParams().set('kind', kind);
    const pid = polygonId?.trim();
    if (pid) params = params.set('polygonId', pid);
    return this.http
      .get<{ success: boolean; data: { kind: ImportUploadKind; files: ImportUploadedFileRecord[] } }>(
        `${this.apiUrl}/import/uploads`,
        { params },
      )
      .pipe(map((res) => res.data?.files ?? []), catchError(this.handleError));
  }

  /** Odstraní záznam importu. Geometrie / ROPID / Unified smaže i objekty vytvořené souborem. */
  deleteImportUploadedFile(
    kind: ImportUploadKind,
    recordId: string,
  ): Observable<ImportUploadDeleteResult> {
    const params = new HttpParams().set('kind', kind);
    return this.http
      .delete<{ success: boolean; data: ImportUploadDeleteResult }>(
        `${this.apiUrl}/import/uploads/${encodeURIComponent(recordId)}`,
        { params },
      )
      .pipe(map((res) => res.data ?? { deleted: false, deletedObjectCount: 0 }), catchError(this.handleError));
  }

  runTrimVykopOdbocky(
    polygonId: string,
    options: { maxHops?: number; tinyM?: number; dryRun?: boolean } = {},
  ): Observable<TrimVykopOdbockyRun> {
    return this.http
      .post<{ success: boolean; data: TrimVykopOdbockyRun }>(
        `${this.apiUrl}/import/trim-odbocky/${encodeURIComponent(polygonId)}`,
        options,
      )
      .pipe(map((res) => res.data), catchError(this.handleError));
  }

  getLatestTrimVykopOdbocky(polygonId: string): Observable<TrimVykopOdbockyRun | null> {
    return this.http
      .get<{ success: boolean; data: TrimVykopOdbockyRun | null }>(
        `${this.apiUrl}/import/trim-odbocky/${encodeURIComponent(polygonId)}/latest`,
      )
      .pipe(map((res) => res.data ?? null), catchError(this.handleError));
  }

  revertTrimVykopOdbocky(runId: string): Observable<TrimVykopOdbockyRun> {
    return this.http
      .post<{ success: boolean; data: TrimVykopOdbockyRun }>(
        `${this.apiUrl}/import/trim-odbocky/runs/${encodeURIComponent(runId)}/revert`,
        {},
      )
      .pipe(map((res) => res.data), catchError(this.handleError));
  }

  getNavaznostBuildingLinks(polygonId: string): Observable<NavaznostBuildingLinkList> {
    return this.http
      .get<{ success: boolean; data: NavaznostBuildingLinkList }>(
        `${this.apiUrl}/navaznost/${encodeURIComponent(polygonId)}`,
      )
      .pipe(map((res) => res.data), catchError(this.handleError));
  }

  analyzeNavaznostLinks(
    polygonId: string,
    payload: unknown,
    manualAssignments?: NavaznostManualAssignments,
  ): Observable<NavaznostAnalyzeResult> {
    const body = this.navaznostImportBody(payload, manualAssignments);
    return this.http
      .post<{ success: boolean; data: NavaznostAnalyzeResult }>(
        `${this.apiUrl}/navaznost/${encodeURIComponent(polygonId)}/analyze`,
        body,
      )
      .pipe(map((res) => res.data), catchError(this.handleError));
  }

  commitNavaznostLinks(
    polygonId: string,
    payload: unknown,
    manualAssignments?: NavaznostManualAssignments,
  ): Observable<NavaznostCommitResult> {
    const body = this.navaznostImportBody(payload, manualAssignments);
    return this.http
      .post<{ success: boolean; data: NavaznostCommitResult }>(
        `${this.apiUrl}/navaznost/${encodeURIComponent(polygonId)}/commit`,
        body,
      )
      .pipe(map((res) => res.data), catchError(this.handleError));
  }

  private navaznostImportBody(
    payload: unknown,
    manualAssignments?: NavaznostManualAssignments,
  ): Record<string, unknown> {
    const source =
      payload && typeof payload === 'object' && !Array.isArray(payload)
        ? (payload as Record<string, unknown>)
        : null;
    const rozvadece = source && 'rozvadece' in source ? source['rozvadece'] : payload;
    const body: Record<string, unknown> = { rozvadece };
    if (source && Array.isArray(source['budovaSpojkoviste'])) {
      body['budovaSpojkoviste'] = source['budovaSpojkoviste'];
    }
    if (source && Array.isArray(source['horizontalniPropoje'])) {
      body['horizontalniPropoje'] = source['horizontalniPropoje'];
    }
    if (manualAssignments && Object.keys(manualAssignments).length > 0) {
      body['manualAssignments'] = manualAssignments;
    }
    return body;
  }

  analyzeProtelcoCatalog(items: ProtelcoCatalogItem[]): Observable<ProtelcoCatalogAnalyzeResult> {
    return this.http
      .post<{ success: boolean; data: ProtelcoCatalogAnalyzeResult }>(
        `${this.apiUrl}/protelco-catalog/analyze`,
        { items },
      )
      .pipe(map((res) => res.data), catchError(this.handleError));
  }

  importProtelcoCatalog(
    items: ProtelcoCatalogItem[],
    fileName?: string,
  ): Observable<ProtelcoCatalogImportResult> {
    return this.http
      .post<{ success: boolean; data: ProtelcoCatalogImportResult }>(
        `${this.apiUrl}/protelco-catalog/import`,
        { items, fileName },
      )
      .pipe(map((res) => res.data), catchError(this.handleError));
  }

  getProtelcoCatalogMeta(): Observable<ProtelcoCatalogMeta> {
    return this.http
      .get<{ success: boolean; data: ProtelcoCatalogMeta }>(`${this.apiUrl}/protelco-catalog/meta`)
      .pipe(map((res) => res.data), catchError(this.handleError));
  }

  analyzePpdReport(polygonId: string, file: File): Observable<PpdAnalyzeResult> {
    return from(file.arrayBuffer()).pipe(
      switchMap((buffer) => {
        const fileBase64 = this.arrayBufferToBase64(buffer);
        return this.http.post<{ success: boolean; data: PpdAnalyzeResult }>(
          `${this.apiUrl}/ppd/${polygonId}/analyze`,
          { fileBase64, fileName: file.name },
        );
      }),
      map((res) => res.data),
      catchError(this.handleError),
    );
  }

  commitPpdReport(polygonId: string, file: File, fileName?: string): Observable<PpdCommitResult> {
    return from(file.arrayBuffer()).pipe(
      switchMap((buffer) => {
        const fileBase64 = this.arrayBufferToBase64(buffer);
        return this.http.post<{ success: boolean; data: PpdCommitResult }>(
          `${this.apiUrl}/ppd/${polygonId}/commit`,
          {
            fileBase64,
            fileName: fileName ?? file.name,
          },
        );
      }),
      map((res) => res.data),
      catchError(this.handleError),
    );
  }

  private arrayBufferToBase64(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]!);
    return btoa(binary);
  }

  deletePpdForPolygon(polygonId: string): Observable<boolean> {
    return this.http
      .delete<{ success: boolean; data: { deleted: boolean } }>(`${this.apiUrl}/ppd/${polygonId}`)
      .pipe(map((res) => res.data?.deleted ?? false), catchError(this.handleError));
  }

  getFinanceComparison(
    polygonId?: string | null,
    opts?: {
      dateFrom?: string;
      dateTo?: string;
      activePolygonOnly?: boolean;
      stavbaPolygonOnly?: boolean;
    },
  ): Observable<FinanceComparisonResult> {
    let params = new HttpParams();
    const pid = polygonId?.trim();
    if (pid) params = params.set('polygonId', pid);
    if (opts?.dateFrom) params = params.set('dateFrom', opts.dateFrom);
    if (opts?.dateTo) params = params.set('dateTo', opts.dateTo);
    if (opts?.activePolygonOnly === true) params = params.set('activePolygonOnly', 'true');
    if (opts?.stavbaPolygonOnly === true) params = params.set('stavbaPolygonOnly', 'true');
    return this.http
      .get<{ success: boolean; data: FinanceComparisonResult }>(
        `${this.apiUrl}/finance-comparison`,
        { params },
      )
      .pipe(map((res) => res.data), catchError(this.handleError));
  }

  getFinanceMaterialComparison(
    polygonId?: string | null,
    opts?: {
      dateFrom?: string;
      dateTo?: string;
      activePolygonOnly?: boolean;
      stavbaPolygonOnly?: boolean;
    },
  ): Observable<FinanceMaterialComparisonResult> {
    let params = new HttpParams();
    const pid = polygonId?.trim();
    if (pid) params = params.set('polygonId', pid);
    if (opts?.dateFrom) params = params.set('dateFrom', opts.dateFrom);
    if (opts?.dateTo) params = params.set('dateTo', opts.dateTo);
    if (opts?.activePolygonOnly === true) params = params.set('activePolygonOnly', 'true');
    if (opts?.stavbaPolygonOnly === true) params = params.set('stavbaPolygonOnly', 'true');
    return this.http
      .get<{ success: boolean; data: FinanceMaterialComparisonResult }>(
        `${this.apiUrl}/finance-comparison/material-lines`,
        { params },
      )
      .pipe(map((res) => res.data), catchError(this.handleError));
  }

  getFinanceWorkComparison(
    polygonId?: string | null,
    opts?: {
      dateFrom?: string;
      dateTo?: string;
      activePolygonOnly?: boolean;
      stavbaPolygonOnly?: boolean;
    },
  ): Observable<FinanceWorkComparisonResult> {
    let params = new HttpParams();
    const pid = polygonId?.trim();
    if (pid) params = params.set('polygonId', pid);
    if (opts?.dateFrom) params = params.set('dateFrom', opts.dateFrom);
    if (opts?.dateTo) params = params.set('dateTo', opts.dateTo);
    if (opts?.activePolygonOnly === true) params = params.set('activePolygonOnly', 'true');
    if (opts?.stavbaPolygonOnly === true) params = params.set('stavbaPolygonOnly', 'true');
    return this.http
      .get<{ success: boolean; data: FinanceWorkComparisonResult }>(
        `${this.apiUrl}/finance-comparison/work-lines`,
        { params },
      )
      .pipe(map((res) => res.data), catchError(this.handleError));
  }

  // ────────────────────────────────────────────────────────────────────
  // Material usage (per-row CRUD pro popup objektu)
  // Source pravdy je `material_usage` tabulka (migrace 068).
  // Permission BE: vlastník (user_id) + admin/superadmin (mirror logiky fotek).
  // ────────────────────────────────────────────────────────────────────

  /**
   * Per-row materiál pro objekt (popup) — vrací položky z phase_change i manual,
   * frontend si je seskupí podle `sourceHistoryId` (= fáze).
   */
  getObjectMaterialUsage(objectId: string): Observable<MaterialUsageRow[]> {
    return this.http.get<any>(`${this.apiUrl}/material-usage/object/${encodeURIComponent(objectId)}`).pipe(
      map(response => Array.isArray(response?.data?.rows) ? (response.data.rows as MaterialUsageRow[]) : []),
      catchError(this.handleError),
    );
  }

  /** Vytvoří nový záznam materiálu (uživatel sobě, nebo admin komukoli). */
  createMaterialUsage(
    payload: CreateMaterialUsagePayload,
    opts?: MaterialUsageHistoryOptions,
  ): Observable<MaterialUsageRow> {
    return this.http.post<any>(`${this.apiUrl}/material-usage`, payload, materialUsageHttpParams(opts)).pipe(
      map(response => {
        if (response?.success && response?.data) return response.data as MaterialUsageRow;
        throw new Error(response?.message || 'Failed to create material usage');
      }),
      catchError(this.handleError),
    );
  }

  /** Upraví záznam materiálu — vlastník svůj, admin libovolný. Posílají se jen měněná pole. */
  updateMaterialUsage(
    id: string,
    payload: UpdateMaterialUsagePayload,
    opts?: MaterialUsageHistoryOptions,
  ): Observable<MaterialUsageRow> {
    return this.http.patch<any>(`${this.apiUrl}/material-usage/${id}`, payload, materialUsageHttpParams(opts)).pipe(
      map(response => {
        if (response?.success && response?.data) return response.data as MaterialUsageRow;
        throw new Error(response?.message || 'Failed to update material usage');
      }),
      catchError(this.handleError),
    );
  }

  /** Soft delete — vlastník svůj, admin libovolný. */
  deleteMaterialUsage(id: string, opts?: MaterialUsageHistoryOptions): Observable<void> {
    return this.http.delete<any>(`${this.apiUrl}/material-usage/${id}`, materialUsageHttpParams(opts)).pipe(
      map(response => {
        if (!response?.success) throw new Error(response?.message || 'Failed to delete material usage');
      }),
      catchError(this.handleError),
    );
  }

  /**
   * Get object status definitions
   */
  getObjectStatusDefinitions(): Observable<ObjectStatusDefinition[]> {
    return this.http.get<ObjectResponse>(`${this.apiUrl}/status/definitions`)
      .pipe(
        map(response => {
          if (response.success) {
            if (Array.isArray(response.data)) {
              return response.data as unknown as ObjectStatusDefinition[];
            }
            return Object.values(response.data);
          }
          return this.getDefaultStatusDefinitions();
        }),
        catchError(this.handleError)
      );
  }

  /**
   * Convert object to map object for display
   */
  convertToMapObject(object: any): MapObject {
    const mapObj: MapObject = {
      id: object.object_id || object.id,
      name: object.object_name ?? object.name ?? (object.object_id || object.id),
      type: object.object_type || object.type,
      status: object.object_status || object.status,
      coordinates: this.extractCoordinatesFromCentroid(object),
      color: getStatusColor(object.object_status || object.status),
      icon: 'object',
      description: object.object_description || object.description,
      metadata: object.object_metadata || object.metadata,
      kiaCode: object.object_kia_code || object.kiaCode,
      polygonId: object.polygon_id ?? object.polygonId ?? object.object_polygon_id,
      polygonName: object.polygonName ?? object.polygon_name ?? null,
      polygonKiaCode: object.polygonKiaCode ?? object.polygon_kia_code ?? null,
    };

    const parentObjectId =
      object.parentObjectId ??
      object.parent_object_id ??
      object.object_parent_object_id ??
      (object as { object_parentObjectId?: string }).object_parentObjectId;
    if (parentObjectId != null && String(parentObjectId).trim() !== '') {
      mapObj.parentObjectId = String(parentObjectId).trim();
    }

    const budovaBuildingType =
      object.budovaBuildingType ??
      object.budova_building_type ??
      (object as { object_budovaBuildingType?: string }).object_budovaBuildingType ??
      (object as { object_budova_building_type?: string }).object_budova_building_type;
    if (budovaBuildingType != null && String(budovaBuildingType).trim() !== '') {
      mapObj.budovaBuildingType = String(budovaBuildingType).trim();
    }
    const junctionSpojkovisteId =
      object.budovaJunctionSpojkovisteId ??
      object.budova_junction_spojkoviste_id ??
      (object as { object_budovaJunctionSpojkovisteId?: string }).object_budovaJunctionSpojkovisteId ??
      (object as { object_budova_junction_spojkoviste_id?: string }).object_budova_junction_spojkoviste_id;
    if (junctionSpojkovisteId != null && String(junctionSpojkovisteId).trim() !== '') {
      mapObj.budovaJunctionSpojkovisteId = String(junctionSpojkovisteId).trim();
    }

    if (object.addressPoint) {
      mapObj.addressPoint = object.addressPoint;
    }
    if (object.parentBudovaAddress) {
      mapObj.parentBudovaAddress = object.parentBudovaAddress;
    }
    if (object.parcelNumber != null || object.parcel_number != null) {
      mapObj.parcelNumber = object.parcelNumber ?? object.parcel_number;
    }
    const rozId =
      object.pripojovanyZafouknutoZRozvadecObjectId ??
      object.bbox_roz_fk_object_id ??
      object.pripojovany_zafouknuto_z_rozvadec_object_id ??
      (object as { object_pripojovany_zafouknuto_z_rozvadec_object_id?: string }).object_pripojovany_zafouknuto_z_rozvadec_object_id;
    const rozName =
      object.pripojovanyZafouknutoZRozvadecName ??
      object.bbox_roz_fk_name ??
      object.pripojovany_zafouknuto_z_rozvadec_name ??
      (object as { object_pripojovany_zafouknuto_z_rozvadec_name?: string }).object_pripojovany_zafouknuto_z_rozvadec_name;
    if (rozId != null && String(rozId).trim() !== '') {
      mapObj.pripojovanyZafouknutoZRozvadecObjectId = String(rozId).trim();
    }
    if (rozName != null && String(rozName).trim() !== '') {
      mapObj.pripojovanyZafouknutoZRozvadecName = String(rozName).trim();
    }
    const rawZafukLength =
      object.zafukLengthM ??
      object.bbox_zafuk_length_m ??
      object.zafuk_length_m ??
      object.object_zafuk_length_m;
    const zafukLength = rawZafukLength == null || rawZafukLength === '' ? null : Number(rawZafukLength);
    if (Number.isFinite(zafukLength)) {
      mapObj.zafukLengthM = zafukLength;
    }
    const rawOkStart =
      object.zafukOkMetrazStartM ??
      object.bbox_zafuk_ok_metraz_start_m ??
      object.zafuk_ok_metraz_start_m ??
      object.object_zafuk_ok_metraz_start_m;
    const okStart = rawOkStart == null || rawOkStart === '' ? null : Number(rawOkStart);
    if (Number.isFinite(okStart)) {
      mapObj.zafukOkMetrazStartM = okStart;
    }
    const rawOkKonec =
      object.zafukOkMetrazKonecM ??
      object.bbox_zafuk_ok_metraz_konec_m ??
      object.zafuk_ok_metraz_konec_m ??
      object.object_zafuk_ok_metraz_konec_m;
    const okKonec = rawOkKonec == null || rawOkKonec === '' ? null : Number(rawOkKonec);
    if (Number.isFinite(okKonec)) {
      mapObj.zafukOkMetrazKonecM = okKonec;
    }
    const zafukRozId =
      object.zafukZRozvadecObjectId ??
      object.bbox_zafuk_roz_fk_object_id ??
      object.zafuk_z_rozvadec_object_id ??
      object.object_zafuk_z_rozvadec_object_id;
    if (zafukRozId != null && String(zafukRozId).trim() !== '') {
      mapObj.zafukZRozvadecObjectId = String(zafukRozId).trim();
    }
    const zafukRozName =
      object.zafukZRozvadecName ??
      object.bbox_zafuk_roz_fk_name ??
      object.zafuk_z_rozvadec_name ??
      object.object_zafuk_z_rozvadec_name;
    if (zafukRozName != null && String(zafukRozName).trim() !== '') {
      mapObj.zafukZRozvadecName = String(zafukRozName).trim();
    }
    const rawZafukStart =
      object.zafukVychoziBod ??
      object.bbox_zafuk_vychozi_bod ??
      object.zafuk_vychozi_bod ??
      object.object_zafuk_vychozi_bod;
    if (rawZafukStart != null) {
      mapObj.zafukVychoziBod = rawZafukStart === true || rawZafukStart === 'true';
    }
    const rawTypOk =
      object.zafukTypOk ??
      object.bbox_zafuk_typ_ok ??
      object.zafuk_typ_ok ??
      object.object_zafukTypOk ??
      object.object_zafuk_typ_ok;
    if (rawTypOk != null && String(rawTypOk).trim() !== '') {
      mapObj.zafukTypOk = String(rawTypOk).trim();
    }
    const meta = object.object_metadata || object.metadata;
    if (meta?.customFields?.infoText != null) {
      mapObj.infoText = meta.customFields.infoText;
    } else if ((object.object_type || object.type) === 'INFO' && (object.object_description || object.description)) {
      mapObj.infoText = object.object_description || object.description;
    }

    // Path: LineString/MultiLineString nebo obvod Polygonu (pro vykreslení jako linie)
    mapObj.path = object.path ??
                  this.extractPathFromGeometry(object.location_geometry) ??
                  this.extractPathFromGeometry(object.location);
    if (object.polygon && Array.isArray(object.polygon) && object.polygon.length >= 3) {
      mapObj.polygon = object.polygon as [number, number][];
    }
    if (object.pathSegments && Array.isArray(object.pathSegments)) {
      mapObj.pathSegments = object.pathSegments as [number, number][][];
    }
    const adj = object.adjacentPoints ?? object.adjacent_points;
    if (adj && typeof adj === 'object') {
      mapObj.adjacentPoints = adj;
    }
    if (object.createdBy != null || object.created_by != null) {
      mapObj.createdBy = object.createdBy ?? object.created_by;
    }
    if (object.updatedBy != null || object.updated_by != null) {
      mapObj.updatedBy = object.updatedBy ?? object.updated_by;
    }
    if (object.updatedAt != null || object.updated_at != null) {
      const at = object.updatedAt ?? object.updated_at;
      if (typeof at === 'string') {
        mapObj.updatedAt = at;
      } else if (at instanceof Date) {
        mapObj.updatedAt = at.toISOString();
      } else {
        mapObj.updatedAt = String(at);
      }
    }
    if (Array.isArray(object.napojeneMtSlots)) {
      mapObj.napojeneMtSlots = object.napojeneMtSlots;
    }
    if (Array.isArray(object.workReservations)) {
      mapObj.workReservations = object.workReservations;
    }
    if (object.acquisition && typeof object.acquisition === 'object') {
      const rawAcqHp = object.acquisition.hpCount;
      const acqHp =
        rawAcqHp == null || rawAcqHp === '' ? null : Math.trunc(Number(rawAcqHp));
      mapObj.acquisition = {
        statusAcq: object.acquisition.statusAcq ?? null,
        souhlasSmlouva: object.acquisition.souhlasSmlouva ?? null,
        ...(Number.isFinite(acqHp) ? { hpCount: acqHp as number } : {}),
      };
    }
    const rawHp =
      object.hpCountTotal ??
      object.hp_count_total ??
      object.bbox_hp_count_total ??
      object.spec?.hpCountTotal ??
      object.specSummary?.hpCountTotal;
    if (rawHp != null && rawHp !== '') {
      const hp = Math.trunc(Number(rawHp));
      if (Number.isFinite(hp) && hp >= 0) mapObj.hpCountTotal = hp;
    }
    const rawDc =
      object.digitalniCesta ??
      object.digitalni_cesta ??
      object.bbox_digitalni_cesta ??
      object.spec?.digitalniCesta;
    if (rawDc != null && rawDc !== '') {
      const dc = Math.trunc(Number(rawDc));
      if (Number.isFinite(dc) && dc >= 0) mapObj.digitalniCesta = dc;
    }
    if (object.photoCount != null && object.photoCount !== '') {
      const photos = Math.trunc(Number(object.photoCount));
      if (Number.isFinite(photos) && photos >= 0) mapObj.photoCount = photos;
    }
    if (typeof object.hasNotes === 'boolean') mapObj.hasNotes = object.hasNotes;
    if (typeof object.hasMaterials === 'boolean') mapObj.hasMaterials = object.hasMaterials;
    if (object.acquisitionSigned != null) {
      mapObj.acquisitionSigned = object.acquisitionSigned === true;
    }
    if (object.acquisitionDocumentUploaded != null) {
      mapObj.acquisitionDocumentUploaded = object.acquisitionDocumentUploaded === true;
    }

    return mapObj;
  }

  /**
   * Extract coordinates from centroid (WGS84) or from Point geometry when centroid missing
   */
  private extractCoordinatesFromCentroid(object: any): [number, number] {
    if (object.centroidLng != null && object.centroidLat != null &&
        Number.isFinite(Number(object.centroidLng)) && Number.isFinite(Number(object.centroidLat))) {
      return [Number(object.centroidLng), Number(object.centroidLat)];
    }
    const loc = object.location_geometry || object.location;
    if (loc?.type === 'Point' && Array.isArray(loc.coordinates) && loc.coordinates.length >= 2) {
      return [Number(loc.coordinates[0]), Number(loc.coordinates[1])];
    }
    return [0, 0];
  }

  /**
   * Extract path from GeoJSON geometry (LineString or MultiLineString)
   */
  private extractPathFromGeometry(geometry: any): [number, number][] | undefined {
    if (!geometry?.type) return undefined;

    if (geometry.type === 'LineString' && Array.isArray(geometry.coordinates)) {
      return geometry.coordinates.map((coord: number[]) => [coord[0], coord[1]] as [number, number]);
    }

    if (geometry.type === 'MultiLineString' && Array.isArray(geometry.coordinates)) {
      const path: [number, number][] = [];
      for (const lineString of geometry.coordinates) {
        if (Array.isArray(lineString)) {
          path.push(...lineString.map((coord: number[]) => [coord[0], coord[1]] as [number, number]));
        }
      }
      return path.length > 0 ? path : undefined;
    }

    return undefined;
  }

  /**
   * Get status color for object
   */

  /**
   * Get default object types - now returns empty array, types come from backend or @cubby/shared
   */
  private getDefaultObjectTypes(): ObjectTypeDefinition[] {
    // Object types are now defined in @cubby/shared and managed via workflow constants
    return [];
  }

  /**
   * Get default status definitions
   */
  private getDefaultStatusDefinitions(): ObjectStatusDefinition[] {
    // Use shared constants to avoid duplication
    return [
      {
        code: STATUS_RED,
        czechName: CZECH_STATUS.NO_INFORMATION.czechName,
        czechDescription: CZECH_STATUS.NO_INFORMATION.czechDescription,
        englishDescription: CZECH_STATUS.NO_INFORMATION.englishDescription,
        color: STATUS_COLORS[STATUS_RED],
        meaning: 'information_completeness'
      },
      {
        code: STATUS_YELLOW,
        czechName: CZECH_STATUS.IN_PROGRESS.czechName,
        czechDescription: CZECH_STATUS.IN_PROGRESS.czechDescription,
        englishDescription: CZECH_STATUS.IN_PROGRESS.englishDescription,
        color: STATUS_COLORS[STATUS_YELLOW],
        meaning: 'information_completeness'
      },
      {
        code: STATUS_GREEN,
        czechName: CZECH_STATUS.COMPLETE.czechName,
        czechDescription: CZECH_STATUS.COMPLETE.czechDescription,
        englishDescription: CZECH_STATUS.COMPLETE.englishDescription,
        color: STATUS_COLORS[STATUS_GREEN],
        meaning: 'information_completeness'
      }
    ];
  }

  /**
   * Get object history timeline with enriched details
   */
  getObjectHistoryTimeline(objectId: string): Observable<any[]> {
    return this.http.get<any>(`${this.apiUrl}/${objectId}/history/timeline`)
      .pipe(
        map(response => {
          if (response.success && Array.isArray(response.data)) {
            return response.data;
          }
          this.logger.warn('Unexpected timeline response format:', response);
          return [];
        }),
        catchError(this.handleError)
      );
  }

  /**
   * Get all photos for an object
   */
  getObjectPhotos(objectId: string, category?: string, photoType?: string): Observable<any[]> {
    let params = new HttpParams();
    if (category) params = params.set('category', category);
    if (photoType) params = params.set('photoType', photoType);

    return this.http.get<any>(`${this.apiUrl}/${objectId}/photos`, { params })
      .pipe(
        map(response => {
          if (response.success && Array.isArray(response.data)) {
            return response.data;
          }
          return [];
        }),
        catchError(this.handleError)
      );
  }

  /**
   * Get photo download URL
   */
  getPhotoUrl(photoId: string): string {
    return `${this.apiUrl}/photos/${photoId}/download`;
  }

  /** Dokumenty objektu: podepsaná akvizice (PDF v MinIO) a sken papírové smlouvy. */
  getObjectDocuments(objectId: string): Observable<Array<{
    id: string;
    name: string;
    createdAt: string;
    kind: string;
    hasFile: boolean;
    driveUrl: string | null;
    photoId: string | null;
    contentType: string | null;
    fileName: string;
  }>> {
    return this.http.get<any>(`${this.apiUrl}/${encodeURIComponent(objectId)}/documents`).pipe(
      map((response) => {
        const data = response?.success ? response.data : response?.data;
        return Array.isArray(data) ? data : [];
      }),
      catchError(this.handleError),
    );
  }

  objectDocumentFileUrl(objectId: string, documentId: string): string {
    return `${this.apiUrl}/${encodeURIComponent(objectId)}/documents/${encodeURIComponent(documentId)}/file`;
  }

  downloadObjectDocument(objectId: string, documentId: string): Observable<Blob> {
    return from(fetchPhotoBlob(this.objectDocumentFileUrl(objectId, documentId)));
  }

  /**
   * Download photo as Blob.
   * Uses fetch with redirect:'follow' so PHOTOS_PRESIGNED_DOWNLOAD=true (API 302 → MinIO)
   * works the same as OFF (API 200 stream). Thumbs via `<img [src]="getPhotoUrl(...)">`
   * already follow redirects in the browser — no change when flag is OFF.
   */
  downloadPhoto(photoId: string): Observable<Blob> {
    return from(fetchPhotoBlob(this.getPhotoUrl(photoId)));
  }

  /**
   * Export polygon objects to DXF (layers by object type; INFO/UNASSIGNED excluded).
   */
  downloadPolygonDxf(polygonId: string, filenameBase?: string): Observable<Blob> {
    let params = new HttpParams();
    const base = filenameBase?.trim();
    if (base) params = params.set('filenameBase', base);
    return this.http
      .get(`${this.apiUrl}/polygon/${encodeURIComponent(polygonId)}/export/dxf`, {
        params,
        responseType: 'blob',
      })
      .pipe(catchError(this.handleError));
  }

  /**
   * Upload photo for object. Fotka se uloží do MinIO + přidá do `object.metadata.photoIds`.
   * Neloguje se `photo_upload` řádek do historie — o zápis historie se postará commit-on-close
   * (`commitSession()`), který zahrne photoIds do jediného `update` / `status_change` řádku.
   */
  uploadPhoto(objectId: string, photoData: { photo: string; category?: string; photoType?: string; description?: string; historyEntryId?: string; statusPhase?: string; applyToPathAdvanceBatch?: boolean }): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/${objectId}/photos/upload`, photoData)
      .pipe(
        map(response => response.success ? response.data : null),
        catchError(this.handleError)
      );
  }

  /**
   * Commit popup session — zapíše jeden řádek do `object_history` pro každý `objectId`
   * se stejným `batchId`. Volá se při zavření popupu (update) nebo potvrzení přechodu
   * fáze (statusTo). Klient během editace ukládá data přes PUT /:id / POST /:id/photos/upload
   * a pamatuje si v paměti, co se změnilo. Na close to pošle sem.
   *
   * Payload:
   *   { objectIds, batchId?, statusTo?, isRevert?, content: { note?, photoIds?, materialUsed?,
   *     excavationData?, weldCountSvaru?, vrTechnicalReport?, spec?, collaboratorEmails?, ... } }
   *
   * Viz backend: `ObjectCommitService.commitSession` a `docs/backend/services/object-service.md`.
   */
  commitSession(body: {
    objectIds: string[];
    batchId?: string;
    /** Celá trasa (včetně lead) — BE vyčistí agregovaná data na superseded lead segmentech. */
    routeSegmentIds?: string[];
    statusTo?: string;
    isRevert?: boolean;
    content: {
      note?: string;
      photoIds?: string[];
      materialUsed?: Array<{ sapCode: string; quantity?: number; warehouseItemId?: string }>;
      excavationData?: Record<string, unknown>;
      weldCountSvaru?: number;
      napojeneMtBarvy?: string[];
      napojeneMtBarvyByBudova?: Record<string, string[]>;
      zafukRozvadecByBudova?: Record<
        string,
        {
          objectId: string | null;
          name: string | null;
          zafukSourceObjectId?: string | null;
          zafukSourceObjectType?: string | null;
          zafukSourceName?: string | null;
        }
      >;
      spojkovisteBodZafuku?: Record<string, unknown>;
      pripojovanyZafukSekundarnichOkPocet?: number;
      vrTechnicalReport?: string;
      spec?: unknown;
      collaboratorEmails?: string[];
      pripojovanyZafouknutoZRozvadecObjectId?: string | null;
      pripojovanyZafouknutoZRozvadecName?: string | null;
      zafukLengthM?: number;
      zafukOkMetrazStartM?: number;
      zafukOkMetrazKonecM?: number;
      zafukZRozvadecObjectId?: string | null;
      zafukZRozvadecName?: string | null;
      zafukSourceObjectId?: string | null;
      zafukSourceObjectType?: string | null;
      zafukSourceName?: string | null;
      zafukVychoziBod?: boolean;
      zafukTypOk?: string;
      zafuknutaMtBarva?: string | null;
      rozvadecZafukEntries?: unknown[];
      budovaWorkflowId?: 7 | 8 | 9;
      babovkaPlacement?: string | null;
      ukonceniVRd?: string | null;
      ukonceniVRdOther?: string | null;
      digitalniCesta?: number | null;
      digitalniCestaSpec?: unknown;
      workflowObjectNotes?: Array<{
        id: string;
        text: string;
        phaseCode: string;
        phaseLabel?: string;
        createdAt: string;
        authorEmail: string;
      }>;
    };
  }): Observable<{ batchId?: string; historyIds: string[]; updatedObjectIds: string[]; polygonIds: string[] }> {
    return this.http.post<any>(`${this.apiUrl}/commit`, body).pipe(
      map((response) => (response?.success ? response.data : null)),
      tap((data) => this.emitCommitCompleted(data)),
      catchError(this.handleError),
    );
  }

  /** Publikuje `commitCompleted$` po úspěšném commitu — ignoruje prázdné odpovědi. */
  private emitCommitCompleted(data: { polygonIds?: string[] } | null): void {
    const raw = data?.polygonIds;
    const ids = Array.isArray(raw) ? raw : [];
    const polygonIds = ids.filter((id) => typeof id === 'string' && id.length > 0);
    if (polygonIds.length === 0) return;
    this.commitCompletedSubject.next({ polygonIds });
  }

  /** BUDOVA: persist drawn odbočka (VYKOP segments + junction spojkoviště). */
  drawBudovaBranch(
    budovaId: string,
    body: {
      junction: { lng: number; lat: number };
      path: [number, number][];
      polygonId: string;
      rozvadecId?: string;
      spojkovisteMaterial?: { sapCode: string; quantity?: number };
    },
  ): Observable<{
    segmentIds: string[];
    spojkovisteId: string | null;
    branchOriginKind?: string;
  }> {
    return this.http.post<{
      success: boolean;
      data?: { segmentIds: string[]; spojkovisteId: string | null; branchOriginKind?: string };
    }>(
      `${this.apiUrl}/budova/${budovaId}/branch-draw`,
      body,
    ).pipe(
      map((res) => {
        if (res?.success && res.data) return res.data;
        throw new Error('Branch draw failed');
      }),
      catchError(this.handleError),
    );
  }

  /** Admin: free-draw VYKOP polyline (hlavní trasa segments, optional junction snap). */
  drawFreeVykop(body: {
    path: [number, number][];
    polygonId?: string;
  }): Observable<{ segmentIds: string[]; polygonId: string }> {
    return this.http.post<{
      success: boolean;
      data?: { segmentIds: string[]; polygonId: string };
    }>(`${this.apiUrl}/vykop/draw`, body).pipe(
      map((res) => {
        if (res?.success && res.data) return res.data;
        throw new Error('VYKOP draw failed');
      }),
      catchError(this.handleError),
    );
  }

  /** Admin: bind SPOJKOVISTE junction to a BUDOVA (map pick). */
  bindSpojkovisteToBudova(
    spojkovisteId: string,
    budovaId: string,
  ): Observable<{
    spojkovisteId: string;
    budovaId: string;
    previousBudovaJunctionSpojkovisteId: string | null;
    alreadyBound: boolean;
  }> {
    return this.http.post<{
      success: boolean;
      data?: {
        spojkovisteId: string;
        budovaId: string;
        previousBudovaJunctionSpojkovisteId: string | null;
        alreadyBound: boolean;
      };
    }>(`${this.apiUrl}/spojkoviste/${spojkovisteId}/bind-budova`, { budovaId }).pipe(
      map((res) => {
        if (res?.success && res.data) return res.data;
        throw new Error('Bind spojkoviště to budova failed');
      }),
      catchError(this.handleError),
    );
  }

  /** BUDOVA: aggregate branch výkop + spojkoviště status for Odbočené UX. */
  getBudovaBranchStatus(budovaId: string): Observable<import('../../features/maps/components/map/core/map.types').BudovaBranchStatusResponse> {
    return this.http.get<{ success: boolean; data?: import('../../features/maps/components/map/core/map.types').BudovaBranchStatusResponse }>(
      `${this.apiUrl}/budova/${budovaId}/branch-status`,
    ).pipe(
      map((res) => (res?.success && res.data ? res.data : {})),
      catchError(this.handleError),
    );
  }

  /**
   * Map stripe prefetch — one RTT for many BUDOVA (avoids N× GET).
   * Returns a map of id → status; missing ids are omitted.
   */
  getBudovaBranchStatusBatch(
    ids: string[],
  ): Observable<Record<string, import('../../features/maps/components/map/core/map.types').BudovaBranchStatusResponse>> {
    const unique = [...new Set(ids.map((id) => id.trim()).filter(Boolean))].slice(0, 100);
    if (unique.length === 0) {
      return of({});
    }
    return this.http.post<{
      success: boolean;
      data?: { statuses?: Record<string, import('../../features/maps/components/map/core/map.types').BudovaBranchStatusResponse> };
    }>(
      `${this.apiUrl}/budova/branch-status/batch`,
      { ids: unique },
    ).pipe(
      map((res) => (res?.success && res.data?.statuses ? res.data.statuses : {})),
      catchError(this.handleError),
    );
  }

  /** BUDOVA: first-click building type selection. */
  setBudovaBuildingType(
    budovaId: string,
    buildingType: BudovaBuildingType,
    opts?: { applyAkviziceDrawingStatus?: boolean },
  ): Observable<{ buildingType: string; status?: string }> {
    const url = `${this.apiUrl}/budova/${budovaId}/building-type`;
    const parse = (res: { success: boolean; data?: { buildingType: string; status?: string } }) => {
      if (res?.success && res.data) return res.data;
      throw new Error('Building type update failed');
    };
    /** Mirrors backend `BudovaBuildingTypeDto`. */
    type BudovaBuildingTypeBody = {
      buildingType: BudovaBuildingType;
      applyAkviziceDrawingStatus?: boolean;
    };
    const post = (body: BudovaBuildingTypeBody) =>
      this.http
        .post<{ success: boolean; data?: { buildingType: string; status?: string } }>(url, body)
        .pipe(map(parse));

    const body: BudovaBuildingTypeBody = { buildingType };
    if (opts?.applyAkviziceDrawingStatus) body.applyAkviziceDrawingStatus = true;

    return post(body).pipe(
      catchError((err) => {
        if (opts?.applyAkviziceDrawingStatus && err?.status === 400) {
          const raw = err?.error?.message;
          const msg = Array.isArray(raw) ? raw.join(' ') : String(raw ?? '');
          if (msg.includes('applyAkviziceDrawingStatus')) {
            return post({ buildingType }).pipe(catchError(this.handleError));
          }
        }
        return this.handleError(err);
      }),
    );
  }

  /** BUDOVA: revert building type when no blocking work (VR / závrt / bábovka). */
  revertBudovaBuildingType(
    budovaId: string,
    body?: { reason?: string },
  ): Observable<{ revertedStatus?: string; previousBuildingType?: string }> {
    return this.http.post<{
      success: boolean;
      data?: { revertedStatus?: string; previousBuildingType?: string };
    }>(
      `${this.apiUrl}/budova/${budovaId}/building-type/revert`,
      body ?? {},
    ).pipe(
      map((res) => (res?.success && res.data ? res.data : {})),
      catchError(this.handleError),
    );
  }

  /** BUDOVA: complete a parallel milestone (ZAVRTANY, ODBOCENO, …). */
  completeBudovaMilestone(
    budovaId: string,
    milestoneId: string,
    body?: {
      babovkaPlacement?: string;
      napojeneMtBarvy?: string[];
      napojeneMtBarva?: string;
      ukonceniVRd?: string;
      ukonceniVRdOther?: string;
      digitalniCesta?: number;
      digitalniCestaSpec?: import('../models/object.model').DigitalniCestaPathSpec[];
    },
  ): Observable<{ milestones?: Record<string, boolean>; autoPhaseCode?: string }> {
    return this.http.post<{ success: boolean; data?: { milestones?: Record<string, boolean>; autoPhaseCode?: string } }>(
      `${this.apiUrl}/budova/${budovaId}/milestones/${milestoneId}`,
      body ?? {},
    ).pipe(
      map((res) => (res?.success && res.data ? res.data : {})),
      catchError(this.handleError),
    );
  }

  /** ROZVADEC: weld-layout v1 aggregate (offers, terminations, links, context). */
  getRozvadecWeldLayout(rozvadecId: string): Observable<RozvadecWeldLayoutResponseJson> {
    return this.http
      .get<{ success?: boolean; data?: RozvadecWeldLayoutResponseJson }>(
        `${this.apiUrl}/${rozvadecId}/rozvadec/weld-layout`,
      )
      .pipe(
        map((res) => {
          if (res?.success && res.data) return res.data;
          throw new Error('Nepodařilo se načíst layout provaření.');
        }),
        catchError((error) => this.throwRozvadecWeldLayoutError(error)),
      );
  }

  getRozvadecRsuOccupancy(rozvadecId: string): Observable<RozvadecRsuOccupancyResponseJson> {
    return this.http
      .get<{ success?: boolean; data?: RozvadecRsuOccupancyResponseJson }>(
        `${this.apiUrl}/${rozvadecId}/rozvadec/rsu-occupancy`,
      )
      .pipe(
        map((res) => {
          if (res?.success && res.data) return res.data;
          throw new Error('Nepodařilo se načíst osazení RAY-TR3.');
        }),
        catchError((error) => this.throwRozvadecWeldLayoutError(error)),
      );
  }

  putRozvadecRsuOccupancy(
    rozvadecId: string,
    body: RozvadecRsuOccupancyPutDto,
  ): Observable<RozvadecRsuOccupancyResponseJson> {
    return this.http
      .put<{ success?: boolean; data?: RozvadecRsuOccupancyResponseJson }>(
        `${this.apiUrl}/${rozvadecId}/rozvadec/rsu-occupancy`,
        body,
      )
      .pipe(
        map((res) => {
          if (res?.success && res.data) return res.data;
          throw new Error('Uložení osazení RAY-TR3 selhalo.');
        }),
        catchError((error) => this.throwRozvadecWeldLayoutError(error)),
      );
  }

  getRozvadecOccupancySchema(rozvadecId: string): Observable<RozvadecOccupancySchemaResponseJson> {
    return this.http
      .get<{ success?: boolean; data?: RozvadecOccupancySchemaResponseJson }>(
        `${this.apiUrl}/${rozvadecId}/rozvadec/occupancy-schema`,
      )
      .pipe(
        map((res) => {
          if (res?.success && res.data) return res.data;
          throw new Error('Nepodařilo se načíst schéma osazení D5.');
        }),
        catchError((error) => this.throwRozvadecWeldLayoutError(error)),
      );
  }

  /** ROZVADEC: replace weld-layout v1 (`expectedRevision` lock). */
  putRozvadecWeldLayout(
    rozvadecId: string,
    body: RozvadecWeldLayoutPutDto,
  ): Observable<RozvadecWeldLayoutResponseJson> {
    return this.http
      .put<{ success?: boolean; data?: RozvadecWeldLayoutResponseJson }>(
        `${this.apiUrl}/${rozvadecId}/rozvadec/weld-layout`,
        body,
      )
      .pipe(
        map((res) => {
          if (res?.success && res.data) return res.data;
          throw new Error('Uložení layoutu provaření selhalo.');
        }),
        catchError((error) => this.throwRozvadecWeldLayoutError(error)),
      );
  }

  /** ROZVADEC: complete a welding milestone (Provaření primárního / sekundárních OK). */
  completeRozvadecMilestone(
    rozvadecId: string,
    milestoneId: string,
    body: { weldCount: number },
  ): Observable<{ milestones?: Record<string, boolean>; autoPhaseCode?: string }> {
    return this.http.post<{ success: boolean; data?: { milestones?: Record<string, boolean>; autoPhaseCode?: string } }>(
      `${this.apiUrl}/rozvadec/${rozvadecId}/milestones/${milestoneId}`,
      body,
    ).pipe(
      map((res) => (res?.success && res.data ? res.data : {})),
      catchError(this.handleError),
    );
  }

  /** ROZVADEC: revert (uncomplete) a welding milestone within 1h window. */
  revertRozvadecMilestone(
    rozvadecId: string,
    milestoneId: string,
    body?: { reason?: string },
  ): Observable<{
    milestoneId?: string;
    milestones?: Record<string, boolean>;
    revertedStatus?: string;
  }> {
    return this.http.post<{
      success: boolean;
      data?: {
        milestoneId?: string;
        milestones?: Record<string, boolean>;
        revertedStatus?: string;
      };
    }>(
      `${this.apiUrl}/rozvadec/${rozvadecId}/milestones/${milestoneId}/revert`,
      body ?? {},
    ).pipe(
      map((res) => (res?.success && res.data ? res.data : {})),
      catchError(this.handleError),
    );
  }

  /** BUDOVA: revert (uncomplete) a parallel milestone within 1h window. */
  revertBudovaMilestone(
    budovaId: string,
    milestoneId: string,
    body?: { reason?: string },
  ): Observable<{
    milestoneId?: string;
    milestones?: Record<string, boolean>;
    revertedStatus?: string;
    branchRevert?: { objectId: string; objectType: string; newStatus: string };
  }> {
    return this.http.post<{
      success: boolean;
      data?: {
        milestoneId?: string;
        milestones?: Record<string, boolean>;
        revertedStatus?: string;
        branchRevert?: { objectId: string; objectType: string; newStatus: string };
      };
    }>(
      `${this.apiUrl}/budova/${budovaId}/milestones/${milestoneId}/revert`,
      body ?? {},
    ).pipe(
      map((res) => (res?.success && res.data ? res.data : {})),
      catchError(this.handleError),
    );
  }

  /**
   * Delete photo from object
   */
  deletePhoto(
    objectId: string,
    photoId: string,
    opts?: { skipHistoryAudit?: boolean },
  ): Observable<void> {
    const params = opts?.skipHistoryAudit ? new HttpParams().set('skipHistoryAudit', '1') : undefined;
    return this.http.delete<any>(`${this.apiUrl}/${objectId}/photos/${photoId}`, { params })
      .pipe(
        map((response) => {
          // Backend returns successResponse format: { success: true, message: '...' }
          if (response?.success === true) {
            return undefined;
          }
          // Fallback for non-standard responses
          const errorMessage = response?.message || 'Delete failed';
          throw new Error(errorMessage);
        }),
        catchError(this.handleError)
      );
  }

  /**
   * Analyze GeoJSON or DXF data and extract layer information (+ detected sourceCrs).
   */
  analyzeGeoJson(
    geoJsonData: any,
    options?: { dxfData?: string; sourceCrs?: string },
  ): Observable<any> {
    const body: {
      geoJsonData?: unknown;
      dxfData?: string;
      sourceCrs?: string;
    } = {};
    if (options?.dxfData) body.dxfData = options.dxfData;
    else body.geoJsonData = geoJsonData;
    if (options?.sourceCrs) body.sourceCrs = options.sourceCrs;
    return this.http.post<any>(`${this.apiUrl}/import/geojson/analyze`, body)
      .pipe(
        map(response => response.success ? response.data : null),
        catchError(this.handleError)
      );
  }

  /**
   * Import objects from GeoJSON data.
   * Backend runs import in background (202); we poll status until completed/failed.
   * options.onProgress(imported, total, phase?) is called when status includes progress.
   */
  importGeoJson(importData: any, options?: { onProgress?: (imported: number, total: number, phase?: string) => void }): Observable<any> {
    return this.http
      .post<any>(`${this.apiUrl}/import/geojson`, importData, { observe: 'response' })
      .pipe(
        timeout(3600000), // 1 hour for upload + polling
        switchMap(res => {
          const body = res.body;
          const jobId = body?.data?.jobId;
          if (res.status === 202 && jobId) {
            return this.pollImportStatus(jobId, options?.onProgress);
          }
          return of(body?.success ? body.data : null);
        }),
        catchError(this.handleError)
      );
  }

  /**
   * Poll import job status until completed or failed; calls onProgress when status has progress.
   */
  private pollImportStatus(jobId: string, onProgress?: (imported: number, total: number, phase?: string) => void): Observable<any> {
    const pollIntervalMs = 2000;
    return timer(0, pollIntervalMs).pipe(
      switchMap(() =>
        this.http.get<any>(`${this.apiUrl}/import/status/${jobId}`).pipe(
          catchError(err => {
            if (err?.status === 404) {
              return throwError(() => new Error('Import job nebyl nalezen (mohl proběhnout restart serveru).'));
            }
            return throwError(() => err);
          })
        )
      ),
      map(res => (res?.success ? res.data : null)),
      tap((data: any) => {
        if (data?.progress && onProgress) {
          onProgress(data.progress.imported, data.progress.total, data.progress.phase);
        }
      }),
      filter((data: any) => data?.status === 'completed' || data?.status === 'failed'),
      take(1),
      map((data: any) => {
        if (data?.status === 'failed') {
          throw new Error(data.error || 'Import selhal');
        }
        return data?.result ?? data;
      }),
      catchError(this.handleError)
    );
  }

  // =============================================================================
  // LAYER MANAGEMENT METHODS
  // =============================================================================


  /**
   * Get all layers for a polygon with object counts
   * @param polygonId - Polygon ID
   * @param includeUnassigned - Whether to include UNASSIGNED objects (default: false)
   */
  getLayersByPolygon(polygonId: string, includeUnassigned: boolean = false): Observable<any[]> {
    const url = includeUnassigned 
      ? `${this.apiUrl}/layers/${polygonId}?includeUnassigned=true`
      : `${this.apiUrl}/layers/${polygonId}`;
    return this.http.get<any>(url)
      .pipe(
        map(response => response.success ? response.data : []),
        catchError(this.handleError)
      );
  }

  /**
   * Check modified objects in a layer. When importFileName is set, only objects from that import file are considered.
   */
  checkModifiedObjects(
    polygonId: string,
    layerName: string,
    importFileName?: string
  ): Observable<any> {
    let url = `${this.apiUrl}/layers/${polygonId}/${encodeURIComponent(layerName)}/check`;
    if (importFileName != null && importFileName !== '') {
      url += `?importFileName=${encodeURIComponent(importFileName)}`;
    }
    return this.http.get<any>(url).pipe(
      map(response => response.success ? response.data : null),
      catchError(this.handleError)
    );
  }

  /**
   * Reassign objects from one layer to a new object type. When importFileName is set, only objects from that import file are reassigned.
   */
  reassignLayer(dto: {
    polygonId: string;
    layerName: string;
    newObjectType: string;
    includeModified?: boolean;
    excludeObjectIds?: string[];
    importFileName?: string;
  }): Observable<{ updated: number; skipped: number }> {
    return this.http.post<any>(`${this.apiUrl}/layers/reassign`, dto).pipe(
      timeout(3600000), // 1 hour – reassign can run long (grouping, densifying)
      map(response => response.success ? response.data : { updated: 0, skipped: 0 }),
      catchError(this.handleError)
    );
  }

  /**
   * Soft-delete all objects in a polygon (admin only). Fails if any object is started.
   */
  deleteObjectsByPolygon(polygonId: string): Observable<{ deleted: number }> {
    return this.http.delete<any>(`${this.apiUrl}/by-polygon/${polygonId}`)
      .pipe(
        map(response => response?.success && response?.data ? response.data : { deleted: 0 }),
        catchError(this.handleError)
      );
  }

  /**
   * Soft-delete object by ID (admin/manager)
   */
  /** Soft delete object. Backend uses authenticated user; only own INFO/SPOJKOVISTE/JAMA or admin can delete. */
  deleteObject(id: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${id}`).pipe(catchError(this.handleError));
  }

  /**
   * Soft-delete celé trasy (path advance segmenty). Pouze admin; backend expanduje na všechny související úseky.
   */
  deleteRoute(segmentIds: string[]): Observable<{ deleted: number; segmentIds: string[] }> {
    return this.http
      .request<{ success?: boolean; data?: { deleted: number; segmentIds: string[] } }>(
        'DELETE',
        `${this.apiUrl}/route`,
        { body: { segmentIds } },
      )
      .pipe(
        map((response) =>
          response?.success && response?.data ? response.data : { deleted: 0, segmentIds: [] },
        ),
        catchError(this.handleError),
      );
  }

  private throwRozvadecWeldLayoutError(error: unknown): Observable<never> {
    const rec = error as { status?: number; error?: { message?: unknown }; message?: string };
    const apiMessage = rec.error?.message;
    const message = Array.isArray(apiMessage)
      ? apiMessage.join(' ')
      : typeof apiMessage === 'string' && apiMessage.trim()
        ? apiMessage
        : typeof rec.message === 'string' && rec.message.trim()
          ? rec.message
          : 'Při ukládání layoutu provaření došlo k chybě.';
    const wrapped = new Error(message) as Error & { status?: number };
    wrapped.status = rec.status;
    return throwError(() => wrapped);
  }

  /**
   * Handle API errors
   */
  private readonly handleError = (error: any): Observable<never> => {
    this.logger.error('Object API Error:', error);

    let errorMessage = 'Při komunikaci se serverem došlo k chybě.';

    const apiMessage = error.error?.message;
    if (Array.isArray(apiMessage)) {
      errorMessage = apiMessage.join(' ');
    } else if (typeof apiMessage === 'string' && apiMessage.trim()) {
      errorMessage = apiMessage;
    } else if (typeof error.message === 'string' && error.message.trim()) {
      errorMessage = error.message;
    }

    return throwError(() => new Error(errorMessage));
  }

}
